/**
 * VERSION MANAGEMENT SYSTEM
 *
 * Build metadata is now injected at build time via Vite's define plugin.
 * See vite.config.ts for the injection logic.
 *
 * VERSION INCREMENT RULES:
 *   - PATCH (0.1.0 -> 0.1.1): Bug fixes, small improvements, minor features
 *   - MINOR (0.1.0 -> 0.2.0): New features, significant improvements
 *   - MAJOR (0.1.0 -> 1.0.0): Breaking changes, major rewrites
 *
 * WORKFLOW:
 *   1. Make code changes
 *   2. Update version in package.json
 *   3. Add entry to CHANGELOG.md
 *   4. Build (version/timestamp/sha auto-injected)
 *   5. Commit with message: "Bump version to X.Y.Z"
 */
// =============================================================================
// Build-time injected values (with fallbacks for dev mode)
// These are replaced by Vite's define plugin at build time
// =============================================================================

export const APP_VERSION =
  typeof __BUILD_VERSION__ !== 'undefined' ? __BUILD_VERSION__ : 'v0.8.6.43';

export const BUILD_TIMESTAMP =
  typeof __BUILD_TIMESTAMP__ !== 'undefined' ? __BUILD_TIMESTAMP__ : '03NOV1917UK';

export const GIT_SHA =
  typeof __GIT_SHA__ !== 'undefined' ? __GIT_SHA__ : 'unknown';

export const VERSION_HISTORY = [
  {
    version: 'v0.8.6.43',
    date: '2026-08-05',
    title: 'Unified AirLabs operations and ticketed-waitlist assurance',
    changes: [
      'Centralized every AirLabs call behind a service-only cached gateway with atomic monthly quota reservation, usage health, and automatic budget protection.',
      'Added managed AirLabs flight listeners, idempotent webhook evidence, and current operational enrichment for status, gates, terminals, baggage, estimates, and aircraft details.',
      'Replaced FlightAware runtime dependencies with the explicit AirLabs current-detail, FlightRadar24 live-position, and Sabre authority hierarchy without inventing unavailable track or reliability data.',
      'Added ticketed Sabre waitlist discovery, fresh-reservation verification, escalating checks toward departure, immutable evidence, AOC alerts, and canonical Ops cases that clear only on explicit confirmed action codes plus active ticket evidence.',
      'Added AirLabs and waitlist health to the desk and office-TV situation room, with focused drill-through to the Ops board and updated in-platform operating procedures.',
    ],
  },
  {
    version: 'v0.8.6.42',
    date: '2026-07-18',
    title: 'Accurate GDS remark audit states and live-read retries',
    changes: [
      'Added explicit missing-from-GDS findings for managed CID, FD, TYPE and PNT elements discovered during audit-only scans.',
      'Stopped classifying incomplete PNT projections or capsules as live GDS conflicts; they now fail closed as unverified platform state with the actual reason.',
      'Made transient live-read failures retry twice before a third terminal unverified result is persisted.',
      'Updated the review cockpit to separate missing, conflict, duplicate, drift and unverified states.',
    ],
  },
  {
    version: 'v0.8.6.41',
    date: '2026-07-18',
    title: 'Human-readable WhatsApp triage notifications',
    changes: [
      'Replaced raw WhatsApp sweep RUNNING and terminal database events with one concise, outcome-focused Slack update.',
      'Suppressed empty successful sweeps while retaining meaningful completions, partial failures, and full failures.',
      'Added durable sweep metrics and readable error serialization so failure alerts explain impact, reason, recovery, and a short trace reference.',
      'Made the database-to-Slack format ASCII-safe with Slack emoji aliases after live mobile verification exposed Unicode corruption.',
    ],
  },
  {
    version: 'v0.8.6.40',
    date: '2026-07-16',
    title: 'Approval-gated GDS remark review and cleanup',
    changes: [
      'Added durable estate-wide live Sabre and Amadeus remark scanning with retry and recovery.',
      'Added a human review cockpit for exact duplicates, conflicts, drift, and unverified PNRs.',
      'Added a fail-closed cleanup executor that re-reads, retains one copy, deletes referenced surplus, and verifies the live GDS.',
      'Added 24-hour monitoring for duplicate blocks and second successful writes.',
    ],
  },
  {
    version: 'v0.8.6.33',
    date: '2026-06-30',
    title: 'Operations Cockpit interface redesign with Multi-Layout support and Telemetry HUD',
    changes: [
      'Feature: Add layout selector to toggle between Columns (dual split), Kanban Board (drag-and-drop), and Spreadsheet Sheet views.',
      'Feature: Implement glassmorphic High Risk Load metrics HUD with SVG circular gauge and clickable priority filters.',
      'Feature: Add right-hand sliding Live Conversation chat panel directly within the DetailSheet to show full message thread.',
      'Feature: Replaced standard browser confirm boxes with custom Verification Modal checklists.'
    ]
  },
  {
    version: 'v0.8.6.32',
    date: '2026-06-30',
    title: 'Ops Cockpit crosschecking, WhatsApp AI triage, and RTW validation fixes',
    changes: [
      'Feature: Implement soft warning validation crosscheck when completing tasks in the Ops Cockpit. Queries live Freshdesk status (via deno edge function) and verifies PNT transaction status (ensures no Open/PartiallyTicketed) and Sabre ticket records, presenting details overlay dialog on mismatch.',
      'Feature: Deploy background cron job + edge function to sweep dk_wa_messages every 2 hours, triaging messages using Claude LLM, linking tickets/PNRs, and auto-inserting DK action items in correct buckets.',
      'Fix: Calibrate RTW validation rules to support booking class A on QR Middle East and AA North American flights. Fixed co-terminal out-and-back spurs to correctly resolve metro sibling airports (e.g. LHR/LGW) while preventing transoceanic bypasses. Verified all 45 Vitest checks passing.',
    ]
  },
  {
    version: 'v0.8.6.31',
    date: '2026-06-26',
    title: 'RTW planner — validate against the FULL canonical engine (not the client subset)',
    changes: [
      'Fix: the /rtw planner validated routings with a client-only engine (rtwRulesEngine.ts) that had just the 11 legacy checks — missing every canonical hard-fail rule (TC progression, exact closure, no-origin-mid-trip, intercontinental caps, per-continent segments, same-direction repeat, transcon, Australia, carrier blacklists) AND all the newer ones (min-2 stopovers, Area-2 cap, surface allowed-pairs, country-of-origin, booking class). It could pass routings Sabre would reject.',
      'The planner now calls the canonical rtw-validate edge function (extended to run the full deterministic diagnosis over structured segments) and displays the authoritative ruleResults + a Rule-coverage panel (enforced / advisory / not-enforced). One engine, one source of truth: planner, troubleshooter, and Assistant all run the identical canonical checks against rtw_fare_rules.',
      'rtw-validate accepts richer segments (booking class, dates/times) and loads airport country, so country-of-origin / surface / booking-class / stopover checks run for the planner too. Refactored diagnostics into diagnoseRtwParsed (shared by the pasted-PNR and structured paths) + structuredToParsed. The client engine remains only a fast preliminary, no longer the rule authority. New unit tests.',
    ]
  },
  {
    version: 'v0.8.6.30',
    date: '2026-06-26',
    title: 'RTW Assistant — accept every input form (PNR locator, PDF, Excel, any text)',
    changes: [
      'Feature: The RTW Assistant now ingests an itinerary from any source. Type a 6-character PNR locator and it fetches the live PNR via sabre-get-reservation; attach a PDF (npm:pdf-parse) or Excel/CSV (npm:xlsx); paste a screenshot (existing vision OCR); or paste any loose route/email — all normalized to segments and run through the deterministic engine.',
      'New _shared/rtw-itinerary-normalizer.ts: a universal AI normalizer that turns arbitrary itinerary text/rows into ordered segments → synthetic PNR for the deterministic parser. The clean-Sabre parser stays the fast path; the normalizer is the fallback. It only converts INPUT — it never decides rule outcomes.',
      'rtw-assistant-chat accepts pnrLocator / fileBase64+fileMime; the chat composer accepts image/PDF/Excel/CSV attachments and treats a lone 6-char token as a locator to pull. The user turn records what was ingested.',
      'Engine stays the judge; same provenance + coverage discipline. New unit test for the synthetic-PNR renderer.',
    ]
  },
  {
    version: 'v0.8.6.29',
    date: '2026-06-26',
    title: 'RTW engine — apply the FULL canonical fare rules + rule-coverage audit',
    changes: [
      'Verified: the canonical fare rules in rtw_fare_rules are COMPLETE, not truncated — all 25+ Sabre RD categories present for DONE3/4/5/6 + DGLOB34; the structured parsed_data already carried fields the engine ignored. This wires them in.',
      'Engine now applies the rules it had been given: minimum-2-stopovers + Area-2 stopover cap (section 08), surface allowed-pair + transoceanic-surface ban (Rule G), country-of-origin international limit (Rule F), and a max-validity check driven by the canonical max_stay_months — all consuming rtw_fare_rules.parsed_data.',
      'New travel-zone resolver (_shared/rtw-travel-zone.ts): airport → ISO country + IATA Area (1/2/3) using the airports table country (100% populated) + a Middle-East country set. The engine airport lookup now carries country. (Note: a country-format split exists in the airports table — major hubs use ISO-2, ~1,070 secondary airports use full country names + lack continent; RTW hubs are ISO-2 + classified, and the resolver safely returns "unknown" for the rest rather than mis-flagging — no false positives.)',
      'New rules-coverage audit (_shared/rtw-rule-coverage.ts) returned on every diagnosis + shown as a "Rule coverage" panel: every category labelled enforced / advisory / not-enforced, with what ran — so coverage is transparent, never a silent pass.',
      'Improves both the standalone troubleshooter and the RTW Assistant. New unit tests for the zone resolver, surface/country checks, and the coverage audit.',
    ]
  },
  {
    version: 'v0.8.6.28',
    date: '2026-06-25',
    title: 'RTW Assistant — booking-class checks, tier-point padding, date scheduling',
    changes: [
      'Feature: The RTW Assistant now validates business booking class against the canonical fare table — e.g. it flags BA770S (S is not valid for DONE business; rebook in D), using extras.bookingClassesBusiness (default D, general fallback B, AA fallback H). Added as a BOOKING_CLASS_FOR_FARE check in rtw-diagnostics (also improves the standalone troubleshooter).',
      'Feature: Tier-point mileage padding — ask "I am shy of my BA target, what can I add that is compliant?" and the Assistant proposes eligible (Fab-6/Fab-4 marketed) out-and-back adds (Asia hubs; Alaska one-in/one-out) with great-circle SM to tier-point math, gated by the per-continent caps (NA 6 / Asia 4) and the 16-segment limit. Reuses scoreStatus + rtw-distance + checkRuleH. New _shared/rtw-pad-optimizer.ts + optimize intent + OptimizationCard.',
      'Feature: Date scheduling — "best dates segment by segment, what to remove" lays out per-segment dates honouring must-visit durations (e.g. Japan/Costa Rica) and a hard return deadline (school half-term), flagging infeasible plans. New _shared/rtw-schedule.ts + schedule intent + ScheduleCard.',
      'These close the gaps the WGFYDH benchmark conversation exposed; the engine already caught the core LAX-DOH-LAX routing failure. Added ANC to the distance table; new unit tests for booking class, padding, scheduling, and a WGFYDH regression benchmark.',
    ]
  },
  {
    version: 'v0.8.6.27',
    date: '2026-06-25',
    title: 'Operations Cockpit Task Categorization & AI Auto-Triage',
    changes: [
      'Feature: Standardized inbox task categories (RTW_QUOTE, RTW_SERVICING, GENERAL_QUOTE, GENERAL_SERVICING, TICKETING, REFUNDS, DISRUPTION, PERSONAL, OTHER).',
      'Feature: Updated SQL database migration overlay and RPC functions (ops_work_items_get, ops_work_items_get_v2) to handle category storage, retrieval, and filter queries.',
      'Feature: Enhanced ops-ai-analyze Edge Function prompt to automatically classify new inbox sweep tasks with Claude.',
      'Feature: Designed category badges, interactive toolbar chips, priority-vs-category grouping toggles, and detail sheets manual category selector.'
    ]
  },
  {
    version: 'v0.8.6.26',
    date: '2026-06-25',
    title: 'RTW Assistant — conversational RTW co-pilot (stateful, never contradicts)',
    changes: [
      'Feature: New conversational RTW Assistant at /rtw/assistant (and the Assistant tab in the RTW agent workspace). A stateful chat over the deterministic engine: paste a PNR/route/screenshot, ask a question ("what does SWP mean?"), challenge a rule ("where exactly does it say AY6665 is banned?"), declare a constraint ("WY is allowed"), or say "build me a DONE4 from OSL keeping MLE, avoid the US". Each turn classifies intent and dispatches to diagnose / validate / refine / cite / build / question.',
      'Anti-contradiction: the thread persists a hypothesis ledger (confirmed / ruled-out / constraints) replayed into every AI prompt server-side, so the assistant never re-raises a ruled-out item or flips a confirmed hard-fail — the #1 complaint about the generic-chatbot approach. New rtw_assistant_threads + rtw_assistant_messages tables.',
      'Authoritative citation: "where does it say AY6665?" returns the literal canonical Sabre RD line (AY FLIGHTS 6651 THROUGH 6760) + fare basis + section from rtw_fare_rules.full_text — not a paraphrase.',
      'Build routings: free-text build requests are structured into a brief (needed_tp mapped deterministically) and run through rtw-research-agent in plan-only mode ($0 GDS) via a new additive assistantBrief path (no Freshdesk ticket required; the FD path is unchanged when absent).',
      'Image input: paste/drop a routing screenshot — OCR via the existing sabre-rtw-vision (Claude vision) into a route, then the normal flow.',
      'Engine: added advisory checks distilled from real RTW Assistance chats — HIDDEN_STOPOVER_24H, MAURITIUS_SOUTH_AFRICA_CARVEOUT, MAX_VALIDITY_12_MONTHS (warnings; Sabre remains the authority). Ingested 29 distilled case-memory lessons from the project chats (case memory now 35 rows). 8 new unit tests (intent classifier + exact-rule citation + advisory rule).',
    ]
  },
  {
    version: 'v0.8.6.25',
    date: '2026-06-25',
    title: 'RTW Troubleshooting Co-Pilot — paste a failing PNR, get a rules-first diagnosis',
    changes: [
      'Feature: New RTW Troubleshooting Co-Pilot. An agent pastes a Sabre PNR (+ optional pricing error + constraints) and gets a deterministic, rules-first diagnosis: line-by-line parse, confirmed rule failures vs ranked hypotheses, similar prior cases from our own sold-RTW history, minimal repair options, a Sabre isolation test plan, and client-safe wording. Reachable at /rtw/troubleshoot (Troubleshoot button on the planner toolbar) and as a Troubleshoot tab inside the RTW agent workspace (prefilled from the draft).',
      'Architecture: Deterministic validator = judge, AI = narrator. The new rtw-troubleshoot-routing edge function REUSES the existing rtw-rules.ts engine (runCanonicalHardFailChecks + validateComprehensiveFareRulesCore) rather than re-deriving rules; the AI layer is sanitised so it can never assert a hard failure the deterministic engine did not flag.',
      'Exactness: every diagnosis runs against the EXACT canonical fare rules (rtw_fare_rules / Sabre RD). The result carries a rules-source badge (canonical vs fallback) with snapshot + version; a fallback run caps confidence, raises RULES_SOURCE_FALLBACK, and shows a "NOT verified" banner — no green verified state on fallback.',
      'New: Deno-side Sabre PNR parser (rtw-pnr-parser.ts) handling glued O&D, OPERATED BY, /DC locators, HX/UN flagging, surfaces, and overnight rollover; pricing-error → rule-code map; deterministic repair planner; case-memory retrieval blending a curated table, real sold RTWs (buildPastRtwCatalogue), and the knowledge base.',
      'Data: new rtw_troubleshooting_sessions + rtw_troubleshooting_case_memory tables (RLS, seeded with 6 canonical lessons). Diagnoses persist so a re-check of the same itinerary returns the same conclusion. Tests: 17 unit tests across the parser and diagnostics, including the exact-rule provenance/fallback paths.',
    ]
  },
  {
    version: 'v0.8.6.24',
    date: '2026-06-25',
    title: 'RTW map — stop constant map re-creation (white map root cause) + plan-only draft crash',
    changes: [
      'Fix (ROOT CAUSE of the white route map, live AND print): MultiViewRouteMap took itineraryItems with a literal `= []` default, which mints a NEW array identity every render. As an effect dependency that re-ran the resolve effect → setMapRoutes/setMapAirports → which the map-init effect depends on, so the entire Mapbox map was torn down (map.remove()) and rebuilt on EVERY render. RTWPlannerPage re-renders frequently (call polling, realtime), so the map never settled long enough to paint — blank live map and a blank print snapshot. Switched the default to a module-level stable empty array so the map only rebuilds on real data changes.',
      'Fix: Loading a plan-only AI draft (the free "Read context & build a plan" path) crashed the whole planner with "Cannot read properties of undefined (reading toUpperCase)". Those drafts carry intent arrays (operating_intent_carriers / preferred_marketing_carriers) but no resolved airline / marketingCarrier, and tier-point calc / fab-4 counters called segment.airline.toUpperCase() unguarded. RTWPlannerPage now backfills the singular carrier fields from the intent arrays on hydration, and tierPointCalculator2025 / rtwOptimizer / sabreHelpers guard the toUpperCase calls.',
    ]
  },
  {
    version: 'v0.8.6.23',
    date: '2026-06-25',
    title: 'RTW print view — fix blank (white) route map snapshot',
    changes: [
      'Fix: The print/share route map came through as a white box (only the Propeller logo overlay showed). handleOpenPrintView switches the map to "print" view immediately before capturing — which RE-CREATES the Mapbox instance — but waitForMapIdle resolved against the OLD already-loaded map and the 300ms buffer was too short, so getCanvas().toDataURL() read a blank, still-loading canvas. Now: wait for React to mint the new map before idling on it, then capture. getMapImage() is async and reads the canvas on a forced render frame (triggerRepaint → on "render") so the preserved drawing buffer always holds real pixels. preserveDrawingBuffer + waitForMapIdle were necessary but not sufficient without this.',
    ]
  },
  {
    version: 'v0.8.6.22',
    date: '2026-06-24',
    title: 'RTW — schema-drift fix, RTW-goal Sabre remark, cost→sell bridge, fare persistence',
    changes: [
      'Feature: new P¥RTWG- tracking remark records which airline + status a RTW trip is flown for (e.g. 5P¥RTWG-BAG = chasing BA Gold, OS P/RTWG-AYP = Finnair Platinum), written the same idempotent way as CID/TYPE/FD via pnr-refresh-on-load and rtw-materialise-draft, parsed by booking-queue-poller, and rendered/copyable in PTRemarkGenerator. Encoded <airlineIATA><tierLetter>, no separator. Added encodeRtwGoal() to _shared/sabre-remarks.ts.',
      'Fix: rtw-materialise-draft was sending its tracking-remark payload at the top level instead of under `context`, so the writer silently wrote nothing — it now writes TYPE/FD/RTWG correctly (CID stays with pnr-refresh-on-load which has the numeric readable_id).',
      'Feature: cost→sell bridge — opening an AI draft in the planner (?ai_draft=) now seeds the Quote panel Net Cost from the agent-priced cost fare (best_total_fare, or cheapest carrier_prices), only when the panel is still empty so advisor input is never clobbered.',
      'Fix: rtw-book-sector now PARSES and PERSISTS the WPRW total fare (best_total_fare / best_currency / best_validating_carrier + booking_state.pricing.total_fare) instead of storing only a boolean priceable flag — the booking finally records what the trip costs.',
      'Fix: schema drift — added committed migrations for the rtw_agent_drafts booking-engine columns (booking_state, pending_decision, codeshare_plan) and ported the whatsapp_chats/whatsapp_messages tables out of migrations_backup into active migrations, so a clean rebuild no longer breaks the booking engine or WhatsApp ingestion. Applied the existing (never-pushed) 3CX call_logs AI columns (transcript/ai_summary/ai_sentiment/ai_action_items) to the live DB.',
      'Improvement: rtw-ticket-brief re-enables 3CX call transcript ingestion now that the call_logs AI columns exist (transcripts flow once ai-analyze-call runs; note call_logs is currently empty — upstream 3CX call-journal ingestion is not yet populating it).',
    ]
  },
  {
    version: 'v0.8.6.21',
    date: '2026-06-24',
    title: 'RTW brief — ingest the customer WhatsApp thread (omnichannel narrative)',
    changes: [
      'Feature: rtw-ticket-brief now pulls the customer\'s WhatsApp thread (whatsapp_messages, matched by customer_id and/or the ticket) and feeds it into the brief chronologically alongside the Freshdesk thread and call notes. The system prompt now instructs the LLM to build ONE timeline across Freshdesk + calls + WhatsApp, where a later WhatsApp message overrides an earlier ticket statement — so a post-meeting change a client sends only on WhatsApp ("move the return a day", "add my wife", "is this routing legal?") is captured, not missed.',
      'Fix: the call_logs SELECT referenced transcript / ai_summary / ai_sentiment / ai_action_items columns that do not exist in this database (the 3CX AI-transcription migration was never applied), which would have silently dropped ALL call rows once selected. Reverted to the columns that exist (metadata + agent_notes). 3CX transcript-TEXT ingestion remains blocked on infra: the transcript column + speech-to-text pipeline are not present in this DB.',
    ]
  },
  {
    version: 'v0.8.6.20',
    date: '2026-06-24',
    title: 'RTW Book — "Re-shop alternatives" actually re-shops (was a dead no-op)',
    changes: [
      'Fix: When a sector came back UC at sell, the operator\'s "Re-shop alternatives" choice only set a dead flag — the next pass re-sold the IDENTICAL flight and UC\'d again, looping forever (FD #51347 LHR-OSL BA768). It now hits live oneworld-business (D) availability for that O&D/date and swaps in the best D-open flight we have not already tried, so the sell actually moves on.',
      'Fix: A re-shopped segment is excluded by its whole DEPARTURE SLOT, not just its flight number — a failed flight and its codeshares are the same metal/seat-pool at the same time (BA768 and its AA7067 codeshare both depart 07:45 and hit the identical UC), so the re-shop now steps to a genuinely different departure (e.g. BA770).',
      'Fix: The re-shopped segment is tagged _confirm_source "sabre-shop:*" so the Book stage\'s resolved-gate keeps the sell buttons unlocked (a bare tag would re-lock the itinerary behind "Resolve flights first").',
      'Verified live on FD #51347: re-shop walked BA768 → AA7067 (same 07:45 metal, correctly skipped) → BA770 (different departure). 12/13 sectors confirmed (HK) in one PNR (WGFYDH), Gold cleared (23,506 TP). The 13th leg (LHR-OSL, 11 Feb 27) is a genuine market wall — every BA departure that day shows cached D but returns UC at sell, and D waitlist is closed — not a system fault.',
    ]
  },
  {
    version: 'v0.8.6.19',
    date: '2026-06-24',
    title: 'RTW Book — auto-recover tight connections at resolve time',
    changes: [
      'Feature: rtw-confirm-book now auto-recovers a connection-time failure instead of dead-ending. When a leg has oneworld-D availability but every option departs before the inbound arrives + 90min MCT, it rolls that leg forward up to 2 days (re-shopping anchored after the inbound) and carries the shift through the rest of the routing so the whole chain stays a legal connection. Resolvable legs are untouched. Verified live on FD #51347 — self-healed DOH-LAX and DFW-MIA (+1 day each), resolved all 13 sectors, still cleared Gold (23,506 TP), no operator/AI steering.',
    ]
  },
  {
    version: 'v0.8.6.18',
    date: '2026-06-24',
    title: 'RTW Book — surface WHY a sector won\'t resolve + persist the resolve trace',
    changes: [
      'Fix: The Book-stage "Resolve & price" showed a bare "Could not resolve flights" because it read r.error (undefined for the unresolved case) instead of r.unresolved. It now names the failing sectors and the reason (e.g. "DOH-LAX: connection-time — all D options depart before the inbound + 90 min MCT").',
      'Improvement: rtw-confirm-book now persists its per-sector resolve trace to tool_trace (success and unresolved paths), so the operator can see which flights were live-verified/changed vs the plan guess, and exactly why a sector failed — in the UI and Sabre Console.',
    ]
  },
  {
    version: 'v0.8.6.17',
    date: '2026-06-24',
    title: 'RTW Agent — realtime no longer wipes the verified plan (cockpit TP + codeshares)',
    changes: [
      'Fix: The workspace realtime subscription replaced the whole draft with payload.new, but Supabase realtime omits/truncates large jsonb on big rows — so an UPDATE (e.g. Approve) blew away codeshare_plan / status_score / proposed_segments. The Book-stage cockpit then fell back to a 25%×SM estimate (showed 11,289 TP and no codeshare/operating badges) instead of the verified 24,531. Realtime now MERGES live columns and preserves the heavy verified fields when the payload omits them.',
    ]
  },
  {
    version: 'v0.8.6.16',
    date: '2026-06-24',
    title: 'RTW cabin bonus — align AA/IB long-haul threshold + label',
    changes: [
      'Fix: The planner (tierPointCalculator2025) used a 3,000 SM long-haul threshold for AA/IB cabin bonus while the authoritative agent scorer (rtw-tier-points) uses 2,000 SM for BA/AA/IB. Aligned the planner to 2,000 SM so the two surfaces agree (BA-program cabin bonus follows BA\'s ~2,000 SM haul band).',
      'Fix: Proposal Points Totals label now reads "Cabin Bonus (BA/AA/IB Marketed)" — the April 2026 cabin bonus applies to AA and IB marketed flights, not BA only.',
    ]
  },
  {
    version: 'v0.8.6.15',
    date: '2026-06-24',
    title: 'RTW Agent — show the researched draft (not the empty shell) + wire Book RTW',
    changes: [
      'Fix: RTWAgentWorkspace now loads the active draft by updated_at and prefers the row that actually carries a researched routing. A forceRefresh re-plan made rtw-ticket-brief insert a new empty brief-shell while the research output was written to the reused draft, so the old created_at ordering surfaced the empty shell and left the real 11-segment routing invisible (empty itinerary, 0 TP).',
      'Fix: The "Book RTW" button in the Proposal stage was a dead no-op (no onClick); it now invokes the approval flow and is disabled until the proposal is ready.',
    ]
  },
  {
    version: 'v0.8.6.14',
    date: '2026-06-24',
    title: 'RTW Amendment Chat — persist thread across re-plans',
    changes: [
      'Fix: Key the Amendment Chat transcript by the stable Freshdesk ticket id instead of draft.id. Each re-plan creates a new draft row, which was resetting the thread every run and dropping the operator messages the cumulative replay depends on. The thread now survives re-plans so prior instructions are always replayed to the agent.',
    ]
  },
  {
    version: 'v0.8.6.13',
    date: '2026-06-24',
    title: 'RTW BA-Gold TP Fix, Persistent Amendment Chat & Quote Fixes',
    changes: [
      'Fix: needed_tp is now derived DETERMINISTICALLY from program + target tier in rtw-ticket-brief (BA Gold = 20,000), overriding the non-deterministic LLM parse that flip-flopped between 5,000 and 20,000 on FD #51347 and let a 5k routing falsely read as "Gold ✓".',
      'Feature: Replaced the one-shot steer box with a persistent, per-draft Amendment Chat (RTWAmendmentChat.tsx) that shows operator messages + the agent\'s result summary and live thinking, and REPLAYS the whole conversation to the agent each re-plan so prior instructions are never forgotten (fixes the steering loops).',
      'Fix: handleClearAll now resets lowestCost; Save now persists lockQuotedAmount; the proposal card no longer shows a hardcoded DONE3 fare basis when none is resolved.'
    ]
  },
  {
    version: 'v0.8.6.12',
    date: '2026-06-24',
    title: 'RTW Steering Live AI Streaming & Cost Breakdown Pricing Labels Fixes',
    changes: [
      'Fix: Passed draftId to runAgent in RTWAgentWorkspace.tsx, allowing the backend to reuse the active draft row during re-plans and enabling realtime postgres updates to stream live AI logs and thinking into the glass window.',
      'Fix: Redesigned the Route Cost Breakdown card in ProposalStage.tsx to display targeted fare basis (e.g. DONE3, DGLOB34) and show clear "Pending GDS booking" status messages when pricing has not run, avoiding misleading mock fallbacks and price duplication.'
    ]
  },
  {
    version: 'v0.8.6.11',
    date: '2026-06-23',
    title: 'Vercel Build Resolution Fix',
    changes: [
      'Fix: Added the untracked RTWAIConversationAuditor.tsx component to Git tracking, resolving Vercel remote build errors caused by missing imports.'
    ]
  },
  {
    version: 'v0.8.6.10',
    date: '2026-06-23',
    title: 'RTW Loyalty Program, Collapsible Banners, Auditor & Pricing Enhancements',
    changes: [
      'Feature: Updated SegmentList to render Fab 6 (BA) or Fab 4/Fav 4 (Finnair/other) program-specific badges and carriers dynamically.',
      'Feature: Wrapped uncollapsed state inside RTWStickySummaryCard so the status banner collapses in sync with the points card.',
      'Feature: Added custom transcript copy-paste to RTWAIConversationAuditor UI, allowing direct manual auditing.',
      'Feature: Updated rtw-conversation-audit Edge Function to parse and append the manual transcript input.',
      'Feature: Integrated lowestCost comparison inputs, margin/savings metrics, and database persistence in RTWQuotePanel and RTWPlannerPage.',
      'Feature: Implemented Lift & Shift customer footnote explanation and date format rendering in RTWSummaryPrintView.'
    ]
  },
  {
    version: 'v0.8.6.9',
    date: '2026-06-23',
    title: 'RTW Map & Print View Rendering Fixes',
    changes: [
      'Fix: Protected generateGreatCircleArc calculations in MultiViewRouteMap.tsx and ATCRouteMap.tsx against identical coords (d < 0.00001) causing NaN values and Mapbox crashes.',
      'Fix: Implemented waitForMapIdle inside MultiViewRouteMap to asynchronously wait for the idle event before capturing print snapshots.',
      'UX: Integrated visual loading and error overlays in MultiViewRouteMap, DetailedRouteMap, and RouteMapPanel for token retrieval diagnostics.'
    ]
  },
  {
    version: 'v0.8.6.8',
    date: '2026-06-23',
    title: 'RTW Planner Extended Quote Panel & Premium Pricing Savings',
    changes: [
      'Feature: Extended RTWQuotePanel to support Net Cost, Markup, Service Fee, and Compare Cost (alternative quote) fields.',
      'Feature: Integrated a smart auto-sum calculation for the total Quoted Amount with a custom manual override Lock toggle.',
      'Feature: Added a premium Pricing Optimization Saved card showing alternative savings with percentages and currency formatting.',
      'UX: Improved the input fields to display reactive currency symbols and live margin percentages.',
      'Database: Automatically persists quote details to itineraries.pnr_data.quote_details on blur events.'
    ]
  },
  {
    version: 'v0.8.6.7',
    date: '2026-06-23',
    title: 'RTW Validating Carrier Optimizer Fare Basis Visualization',
    changes: [
      'Feature: Enhanced Validating Carrier Optimizer component to extract and render the validated fare basis code (e.g., DONE4, DGLOB34) for each successful carrier.',
      'Feature: Updated both Sabre and Amadeus carrier pricing edge functions (sabre-rtw-carrier-pricing and amadeus-rtw-carrier-pricing) to parse and return the fare basis code in their payloads.',
      'UX: Displayed the validated fare basis code clearly on both individual carrier cards and the Best Option summary banner.'
    ]
  },
  {
    version: 'v0.8.6.6',
    date: '2026-06-23',
    title: 'Stripe Customer Sync Deduplication & Error Propagation',
    changes: [
      'Feature: Implemented query-before-create email matching in Stripe sync functions to select and link the oldest existing Stripe customer instead of creating duplicates.',
      'Fix: Corrected Deno environment Stripe secrets resolution to support STRIPE_UK_SECRET_KEY and STRIPE_US_SECRET_KEY.',
      'Fix: Improved sync processor error handling to log and propagate failed Stripe operations to the database instead of failing silently.',
      'Database: Cleaned up and re-queued 433 unlinked customers to trigger clean database-to-Stripe sync retry.'
    ]
  },
  {
    version: 'v0.8.6.5',
    date: '2026-06-23',
    title: 'Strict Exact Airport Matching & JL/AA Carrier Preferences',
    changes: [
      'Feature: Implemented strict airport code matching in the RTW confirmation pipeline to filter out co-terminal airports (e.g. ONT instead of LAX) and prevent GDS pricing/booking mismatches (Sabre ‡CTY‡ errors).',
      'Feature: Programmed codeshare selector to prioritize JL-operated flights (especially A350) and book directly under the JL flight number (no codeshare needed) to maximize Tier Points.',
      'Feature: Configured direct AA flight numbers for AA-operated segments, bypassing unnecessary Finnair (AY) codeshares.',
      'Feature: Enabled service_role authentication bypass in both rtw-confirm-book and sabre-enhanced-availability edge functions to allow secure backend and local script verification.'
    ]
  },
  {
    version: 'v0.8.6.4',
    date: '2026-06-22',
    title: 'RTW Segment Carrier Mapping Fix',
    changes: [
      'Bug Fix: Added fallback resolution for segment operating and marketing carriers to support preferred_marketing_carriers and operating_intent_carriers array formats, resolving the 0 TP rendering bug on flight list cards.'
    ]
  },
  {
    version: 'v0.8.6.3',
    date: '2026-06-22',
    title: 'Interactive Premium Glassmorphic RTW Workspace Redesign',
    changes: [
      'Feature: Redesigned the RTW Proposal Stage (ProposalStage.tsx) workspace into a premium glassmorphic 3-column layout.',
      'Feature: Added a horizontal connections timeline visualizer showing the transit path of airport stops.',
      'Feature: Integrated lock/unlock icons on flight segments to enforce operator constraint overrides during AI routing.',
      'Feature: Programmed live points calculator sandbox supporting instant partner Tier Points & Cabin Bonus re-simulation.',
      'Feature: Added a retro terminal optimization console showing step-by-step AI tool traces.',
      'Feature: Decoupled and filtered internal questions to post clean client-ready routing reports to Freshdesk.'
    ]
  },
  {
    version: 'v0.8.6.2',
    date: '2026-06-22',
    title: 'Mapbox Directions Driving Routes & Traveler Vault',
    changes: [
      'Feature: Expand map rendering in MultiViewRouteMap to dynamically fetch road routing from Mapbox Directions API for driving/walking transfers and car rentals.',
      'Feature: Added routing override settings for transfers and car rentals in the trip builder timeline editor (driving, walking, transit, straight-line).',
      'Feature: Enabled secure digital signatures only for client approvals and decoupled Stripe checkouts to separate CRM invoicing.',
      'Feature: Organization of Content Catalog with My Saved Items vs Global Library tabs.'
    ]
  },
  {
    version: 'v0.8.6.1',
    date: '2026-06-22',
    title: 'Interactive RTW Co-Pilot Workspace & Realtime Logs',
    changes: [
      'Feature: Respect locked segments as immutable constraints during AI route re-planning.',
      'Feature: Subscribed to tool_trace realtime database changes for live agent thinking log streaming.',
      'Feature: Client-side sandbox simulator instantly recalculates partner Tier Points and Cabin Bonuses on manual airport/carrier overrides.',
      'Feature: Explicit control with "Post Route to Freshdesk" button to publish route notes directly.',
      'Feature: Clean report generation omitting internal open questions from agent deliverables.'
    ]
  },
  {
    version: 'v0.8.6.0',
    date: '2026-06-21',
    title: 'BA Cabin Bonus and Background Research Execution',
    changes: [
      'Feature: Integrated BA April 2026 Cabin Bonus calculations into the Codeshare Planner, separating partner earning and cabin bonus.',
      'Feature: Added background execution path to rtw-research-agent using EdgeRuntime.waitUntil and self-invocations to avoid platform timeouts.',
      'Feature: Added direct in-memory scoreStatus calls in rtw-research-agent, bypassing status-score edge function overhead.',
      'Bug Fix: Written status_score to database during plan-only mode, resolving "No status score (program unresolved)" issue.',
      'Bug Fix: Checked status goal target matching for both Finnair and BA programs using computed grand totals.'
    ]
  },
  {
    version: 'v0.8.5.9',
    date: '2026-06-19',
    title: 'Diverse RAG RTW Templates In Planner Agent',
    changes: [
      'Feature: Enhanced RTW Planner RAG context pipeline to dynamically classify and load a balanced mix of 8 historical template itineraries covering Eastbound, Westbound, Finnair (AY) focused, and British Airways (BA) focused routes.',
      'Integration: Added static major longitude map and classification helpers (detectDirection, detectTargetProgram) to categorize historical sold RTWs.',
      'Deployment: Deployed updated rtw-research-agent edge function to Supabase.'
    ]
  },
  {
    version: 'v0.8.5.8',
    date: '2026-06-19',
    title: 'Historical Sold RTW Itinerary Templates',
    changes: [
      'Feature: Added a dedicated panel (SoldRtwTemplatesPanel) to search, filter, and load historical sold RTW bookings from the database directly into the planner workspace.',
      'Feature: Fetches live PNR records dynamically where journey_shape = \'rtw\', displaying passenger names, locators, segment counts, fares, and route paths.',
      'Integration: Programmed automatic segment mapper to convert database PNR segment fields into clean FlightSegment planner objects.'
    ]
  },
  {
    version: 'v0.8.5.7',
    date: '2026-06-19',
    title: 'RTW Planner Segment Bonuses & Fare Storage',
    changes: [
      'Feature: Added toggle in the RTW planner to include/exclude the April 2026 BA/AA/IB segment bonuses in BA Tier Points calculations.',
      'Feature: Added "Store Fare" option for successfully priced validating carriers using Sabre cryptic WPRW sequence.',
      'Feature: Display stored PQs on the PNR directly within the RTW pricing panel with a refresh option.',
      'Bug Fix: Resolved RTW validation failures for shell PNRs and Sabre day-of-week digit segment parsing.',
      'Bug Fix: Refactored continent lookups across validation engines to use DB-backed airport database.'
    ]
  },
  {
    version: 'v0.8.5.6',
    date: '2026-06-19',
    title: 'RTW Planning System BA Gold & FAB6 Optimizations',
    changes: [
      'Feature: Recalibrated BA Executive Club tier point status thresholds (Bronze: 3,500, Silver: 7,500, Gold: 20,000) to support correct status matching.',
      'Feature: Expanded codeshare planner to use the full FAB6 carriers (BA/AA/IB/AY/QR/JL) for high-yield British Airways program targeting.',
      'Bug Fix: Added automatic degraded yield detection and BA codeshare swap suggestions for BAEC plans on carriers outside the FAB6 group.'
    ]
  },
  {
    version: 'v0.8.5.5',
    date: '2026-06-18',
    title: 'CRM Create Sabre PNR Shell RTW & Manual Passenger Enhancements',
    changes: [
      'Feature: Added Round the World (RTW) PNR checkbox on Step 2 to execute QR ONEW.YY and YY OW RTW Sabre OSI remarks.',
      'Feature: Added "+ Add Passenger" button at the bottom of the passenger details in Step 2 to manually add passenger cards.',
      'Feature: Added "Remove" button next to each passenger details card in Step 2 to dynamically remove passenger rows.'
    ]
  },
  {
    version: 'v0.8.5.4',
    date: '2026-06-17',
    title: 'Sabre Booking Segment Index Calculation Fix',
    changes: [
      'Bug Fix: Corrected segment index calculation for UC segment cancellation inside the ER_AUTO_ACK macro (sabre-agent-workflow).',
      'Bug Fix: Now dynamically checks if a segment was actually created in the work area, ignoring city ambiguity and carrier name check prompt responses.',
      'Verification: Prevents incorrect segment cancellation and ensures that valid sold segments (SS) are committed.'
    ]
  },
  {
    version: 'v0.8.5.3',
    date: '2026-06-17',
    title: 'CRM Create Sabre PNR Shell Enhancements',
    changes: [
      'Feature: Retrieve Frequent Flyer numbers for primary and related passengers directly from traveler_loyalty_programs.',
      'Feature: Updated default retention segment hold date to 330 days.',
      'Feature: Added Freshdesk ticket association input field and dynamic *FD suffix generation for passenger names.',
      'Bug Fix: Postponed parent page data reload in the success callback to click of the Done button, preventing premature modal unmounting and resetting.',
      'Bug Fix: Configured edge function to skip traveler database merging when includeCustomerTravelers is false.'
    ]
  },
  {
    version: 'v0.8.5-1',
    date: '2026-06-13',
    title: 'RTW Agent Booking Fix — customerId / GDS-cost prompts',
    changes: [
      'Fix CRITICAL: RTW "Book all" failed with "customerId is required" — customer auto-creation hit a partial-index ON CONFLICT bug; replaced with race-safe select-then-insert in shared _shared/rtw-ensure-customer.ts.',
      'Fix: rtw-book-sector now self-heals a missing customer link before creating the PNR shell (survives a plan built before the CRM row existed).',
      'Fix: freshdesk-crm-ingest stopped silently dropping every contact (same ON CONFLICT bug).',
      'UX: Removed the "This SPENDS GDS" confirmation pop-ups from the RTW Book stage.',
    ]
  },
  {
    version: 'v0.8.5.2',
    date: '2026-06-11',
    title: 'DashboardLayout TDZ ReferenceError Fix',
    changes: [
      'Bug Fix: Relocated route evaluation useEffect hook below baseNavigationSections to prevent runtime Temporal Dead Zone reference error on minified builds.'
    ]
  },
  {
    version: 'v0.8.5.1',
    date: '2026-06-11',
    title: 'PNT & Route Permission Hardening',
    changes: [
      'Security: Patched Google SSO signup null-type loophole by adding a BEFORE INSERT OR UPDATE database trigger to automatically map user types.',
      'Security: Hardened route permissions to restrict /pnt/*, /finance/*, and /admin/* views dynamically on both database and client levels.',
      'UX: Restructured sidebar navigation and top-level tabbed navigation to dynamically hide empty folders/tabs and scale grid layouts.'
    ]
  },
  {
    version: 'v0.8.5.0',
    date: '2026-06-09',
    title: 'Permission Center & User Management Hardening',
    changes: [
      'Feature: Synced all 109 core features and role/route permissions to resolve Permission Center silent blocks.',
      'Feature: Upgraded FeatureSyncStatus to render descriptive error warnings directly in the sync UI on database mismatches.',
      'Bug Fix: Patched admin-list-users edge function to return full user records with accurate last sign-in timestamps.',
      'UX: Hardened UserManagement role selector to allow super admins to edit roles on other profiles while protecting self-lockout.'
    ]
  },
  {
    version: 'v0.8.4.9',
    date: '2026-06-09',
    title: 'Knowledge Base Copilot & Admin Review Integration',
    changes: [
      'Feature: Integrated Agent Q&A Copilot into the communications ticket sidebar and communications dashboard right pane.',
      'Feature: Programmed custom DOM event ucc-insert-reply-content to cleanly inject suggested KB articles into ReplyComposer.',
      'Feature: Upgraded KBEntryDetail to a premium side-by-side split layout embedding the KBReviewChat Claude assistant.',
      'UX: Enabled direct synchronization of Claude suggestions back into editor fields upon choosing Enhance in audit chat.'
    ]
  },
  {
    version: 'v0.8.4.8',
    date: '2026-06-09',
    title: 'Real-Time Omnichannel Sync Hardening',
    changes: [
      'Feature: Hardened freshdesk-webhook-handler to parse and upsert tickets directly into comm_tickets and contacts into comm_contacts.',
      'Feature: Enabled real-time UCC dashboard list updates matching external ticket/status updates on Freshdesk without periodic delays.',
      'Bug Fix: Resolved customer mapping lookup block inside webhook handling to guarantee all tickets sync even for new unregistered customers.'
    ]
  },
  {
    version: 'v0.8.4.7',
    date: '2026-06-09',
    title: 'AI Agent Copilot & Tone Studio Integration',
    changes: [
      'Feature: Created agent persona management schema and RLS policies supporting customized AI personalities.',
      'Feature: Developed automatic draft comparison and continuous style learning logic (ucc-ghostwriter-learn).',
      'Feature: Enhanced Magic Draft creator with RAG knowledge lookups and live Sabre PNR details.',
      'UI: Integrated selected Writing Persona dropdown and continuous style learning status notice into the Composer.'
    ]
  },
  {
    version: 'v0.8.4.6',
    date: '2026-06-08',
    title: 'Platform Navigation Alignment & Legacy Code Cleanup',
    changes: [
      'Cleanup: Deleted legacy, unused UnifiedQueue dashboard directory and CommunicationHub components.',
      'Refactor: Reorganized sidebar tabs - moved Tasks & Automation to CRM tab, and renamed Planning Tools to Loyalty & Planner.',
      'Feature: Integrated Amadeus AI Agent under Intelligence section and Portal Settings under Public Portal in the sidebar.',
      'Feature: Exposed Automation Settings button in User Preferences next to Connections & Integrations.',
      'Routing: Canonicalized duplicate routes by establishing Navigate redirects in the Router for 10+ legacy paths (including /communication-hub, /quality-control, /checkin).'
    ]
  },
  {
    version: 'v0.8.4.5',
    date: '2026-06-08',
    title: 'Dashboard Widgets Optimization & Fallback Resolution',
    changes: [
      'Feature: Resolved empty Traveler Map widget by geocoding null coordinates from the airports table.',
      'Feature: Resolved empty Risk Alerts card by checking city/country names of active traveler destination airports.',
      'Feature: Configured Risk Alerts to fall back to top global critical/high advisories if no destinations match.',
      'Bug Fix: Patched Needing Attention widget to query open/pending interactions and sort by urgency, then by oldest first.',
      'Bug Fix: Updated v_suspect_parsed_pnrs database view to include ticket extraction failures in the Suspect Fares widget.',
      'Integration: Programmed AOC Summary widget to trigger background syncFromTrips() on mount to populate active flights.'
    ]
  },
  {
    version: 'v0.8.4.4',
    date: '2026-06-07',
    title: 'CRM Audits & Premium Right Slide-out Drawers Integration',
    changes: [
      'Feature: Converted centered CRM modals for traveler loyalty programs and traveler profiles into premium right slide-out drawers',
      'Feature: Styled loyalty program and traveler drawers with Aviation Blue (#0F4C81) and Gold (#D4AF37) branding colors',
      'Refactor: Replaced legacy AddLoyaltyProgramModal and AddTravelerModal with AddLoyaltyProgramDrawer and AddTravelerDrawer'
    ]
  },
  {
    version: 'v0.8.4.3',
    date: '2026-06-07',
    title: 'Dashboard Metrics & Queue Queries Fallback Fix',
    changes: [
      'Bug Fix: Redirected pending ticketing count and deadlines queries in QuickStatsBar and QueueService to utilize the populated pnt_transactions table instead of the empty pnt_bookings table.',
      'Bug Fix: Corrected today\'s revenue calculation column mismatch in QuickStatsBar to reference total_fare_amount instead of total_fare.',
      'Data: Populated user_id fields on existing customer_trips to match their associated PNR records, enabling the AOC sync orchestrator to identify and sync active traveler flights.'
    ]
  },
  {
    version: 'v0.8.4.2',
    date: '2026-06-07',
    title: 'Dashboard Layout Reset Fix',
    changes: [
      'Bug Fix: Added the Reset Layout button directly to the dashboard editing bar to resolve saved database layout overrides.',
      'Aesthetics: Corrected Tailwind color classes to guarantee proper button rendering.'
    ]
  },
  {
    version: 'v0.8.4.1',
    date: '2026-06-06',
    title: 'Dashboard Optimization & Subsystem Integration',
    changes: [
      'Feature: Live Revenue Trend (Area), PNT Aging (Bar), and Carrier Segment Share (Pie) widgets using Recharts',
      'Feature: Real-time Supabase postgres updates hook automatically refreshing widget contents',
      'Feature: Inline payment link generation, activation, and copy actions for Unbilled Service Fees',
      'Feature: Inline PNR re-parsing and GDS validation trigger for Suspect parsed Fares',
      'Feature: Dynamic traveler map filters by status with Metar weather category overlays (VFR/IFR/LIFR)',
      'Feature: Role-based default grids customized automatically for Finance, Operations, and Admin users'
    ]
  },
  {
    version: 'v0.8.4.0',
    date: '2026-06-06',
    title: 'World-Class CRM Integrations, Amadeus Parity, Sonnet 4.6 Migration & Proactive IRROPS',
    changes: [
      'Feature: Upgraded default AI models in all workflows to Anthropic Claude 3.7 Sonnet (Sonnet 4.6)',
      'Feature: Consolidated GDS retrieval routing into pnr-gateway fetch-failover with automatic CRM Trip matching and upserts',
      'Feature: Integrated proactive FlightStats delay/cancellation disruption alerts in CRM Customer Detail and PNR views',
      'Feature: Expanded UCC replies to route WhatsApp messages directly to the whatsapp-send-notification endpoint'
    ]
  },

  {
    version: 'v0.8.3.3',
    date: '2026-06-05',
    title: 'CRM Drawers & Visual Language Unification',
    changes: [
      'Feature: Replaced B2B company, lead, and sales opportunity modals with slide-out right drawers',
      'Feature: Standardized corporate and lead creation drawers to use Indigo theme and rounded-xl containers',
      'Feature: Integrated debounced company name/email duplicate preflight checks and lead database warnings',
      'Refactor: Replaced legacy AddOpportunityModal with AddOpportunityDrawer across client views'
    ]
  },
  {
    version: 'v0.8.3.2',
    date: '2026-06-05',
    title: 'Vercel Deployment Refresh & Version Bump',
    changes: [
      'Chore: Bumped build version to v0.8.3.2 and updated timestamps to trigger Vercel deployment refresh.'
    ]
  },
  {
    version: 'v0.8.3.1',
    date: '2026-06-05',
    title: 'CRM Ingestion, Auditing & Automated Sync',
    changes: [
      'Feature: Unified CRM Add Customer Slide-out Drawer with preflight duplicate checks and PNT/GDS matching notices',
      'Feature: Passenger matrices splits support for Sabre SOAP and Amadeus booking imports',
      'Feature: Automated background sync cron trigger running every 30 minutes to backfill and enrich Freshdesk data',
      'Feature: Deep AI customer enrichment extracting companion traveler relations and marketing intelligence insights',
      'Refactor: Consolidated Freshdesk classification logic into shared module, enforcing blacklist filters for internal domains'
    ]
  },
  {
    version: 'v0.8.2.140',
    date: '2026-04-19',
    title: 'Platform Navigation & UI',
    changes: [
      'Feature: Edge-to-edge global workspace with new Collapsible Sidebar for high-density tables (like PNT)',
      'Cleanup: Refreshed manual documentation with edge-to-edge screenshots for better legibility'
    ]
  },
  {
    version: 'v0.8.2.139',
    date: '2026-04-17',
    title: 'Advanced System Manual & Mermaid Diagrams',
    changes: [
      'Feature: Searchable System Manual for super admins mapping to rich markdown registry',
      'Feature: Mermaid Diagram rendering directly in markdown for technical architecture outlines',
      'Feature: Deep Technical Overhaul of PNT Documentation (Schemas, Flow Charts, DB relationships)',
    ]
  },
  {
    version: 'v0.8.2.139',
    date: '2026-04-17',
    title: 'Trip Creation Constraint Fix',
    changes: [
      'Fix CRITICAL: Resolved database constraint error (trips_financial_status_check) that prevented creating new trips with unpaid statuses',
    ]
  },
  {
    version: 'v0.8.2.139',
    date: '2026-04-16',
    title: 'Phase 2 & 3: Superhuman Omniscience & Magic Links',
    changes: [
      'Feature: Pax Platform (pax.propellertravel.com) now supports Magic Link secure authentication without SSO',
      'Feature: Passenger Dashboard securely injected with JWT-authenticated profiles upon valid token verification',
      'Feature: AI Cryptic Filtering translates Sabre penalty strings directly into JSON (Claude 3.5 Sonnet)',
      'Feature: Autonomous GDS Failover seamlessly bridges Amadeus and Sabre parsing drops',
      'Feature: Dead Letter Queue (DLQ) ensures asynchronous tasks retry automatically to prevent data loss',
    ]
  },
  {
    version: 'v0.8.2.139',
    date: '2026-04-16',
    title: 'AerTicket & PNR Automated Sync',
    changes: [
      'Feature: AerTicket statements are natively bridged to PNT Tracker for actual costs and margins',
      'Feature: Zero-cost or voided invoices automatically transition the PNT transaction to Voided/Refunded status',
      'Feature: Daily PNR Refresh triggers automatically update unticketed bookings to prevent stale alerts',
    ]
  },
  {
    version: 'v0.8.2.139',
    date: '2026-04-16',
    title: 'Gmail Import Timeout Fix',
    changes: [
      'Fix CRITICAL: Resolved Gmail OPL statement import 504 Gateway Timeout by replacing a single heavy API request with frontend batched polling (batches of 2).',
    ]
  },
  {
    version: 'v0.8.2.134',
    date: '2026-04-11',
    title: 'PNR Enrichment & Match Fixes',
    changes: [
      'Fix CRITICAL: Resolved PNR Match failure by correctly splitting surname/first name titles (MR/MRS) during CRM customer comparison.',
      'Fix: Freshdesk PNR Auto-Enrichment 502 AppError crash bypassed by integrating direct `smartSearch` over deprecated `searchTickets` double-quote pattern.',
      'Fix: PNR Unified Ingest Out-Of-Memory (CORS) exceptions prevented by utilizing `pnrLocator` argument bounds down to the specific database edge function query.',
    ]
  },
  {
    version: 'v0.8.2.134',
    date: '2026-04-11',
    title: 'Unified Master Search (Cmd+K)',
    changes: [
      'Feature: Universal Command Palette (Cmd+K) connecting Navigation, PNRs, CRM Entities, and Invoices.',
      'Feature: Context-aware actions based on current page view (e.g. Add Booking while viewing a customer).',
      'Feature: Fast keyboard traversal (j/k) and category filtering.',
      'Cleanup: Replaced legacy GlobalSearch and CommandPalette components.',
    ]
  },
  {
    version: 'v0.8.2.132',
    date: '2026-04-03',
    title: 'Amadeus RTW Validating Carrier Optimizer',
    changes: [
      'Feature: Validating Carrier analysis now fully supports Amadeus PNRs via FXX without creating clutter',
      'Feature: UI toggle seamlessly switches between Sabre and Amadeus syntax logging',
    ]
  },
  {
    version: 'v0.8.2.131',
    date: '2026-04-03',
    title: 'Universal Queue Ingestion & Silo Elimination',
    changes: [
      'Feature: Queue worker dynamically polls Amadeus and Sabre queues using centralized DB configs',
      'Feature: Zero-Orphan Ingestion auto-generates CRM Shadow Profiles for unmatched records',
      'Feature: Unified Comms (UCC) Integration automatically creates system tickets for review agents',
      'Cleanup: Entirely eliminated the legacy standalone Unmatched PNR report and tab UI'
    ]
  },
  {
    version: 'v0.8.2.130',
    date: '2026-03-15',
    title: 'Supplier Invoices UI Crash Fix',
    changes: [
      'Fix CRITICAL: Resolved Temporal Dead Zone (Cannot access Q before initialization) crash rendering the Supplier Invoices page unusable in production',
    ]
  },
  {
    version: 'v0.8.2.130',
    date: '2026-03-15',
    title: 'Alaska Airlines Tier Points Integration',
    changes: [
      'Feature: Alaska Airlines Atmos Rewards integration into RTW Optimizer Pro',
      'Feature: Validating Alaska status points on PNR Import View',
      'Feature: Added Alaska column to Tier Points Calculator page',
    ]
  },
  {
    version: 'v0.8.2.122',
    date: '2026-03-05',
    title: '3CX Integration Fixes',
    changes: [
      'Fix CRITICAL: 3CX Webhook crashing with `500 user_id constraint`. Now dynamically resolves owner using auth admin API.',
      'UI: Softphone Widget gracefully handles missing configuration with pop-out support to bypass iframe security blocks.',
    ]
  },
  {
    version: 'v0.8.2.120',
    date: '2026-03-03',
    title: 'Ghostwriter Migration to OpenRouter',
    changes: [
      'Refactor: Migrated the `ucc-ghostwriter` Edge Function to use OpenRouter (Claude 3.5 Sonnet) instead of standard OpenAI.',
      'Infra: Added `ucc-ghostwriter` to the automated Supabase GitHub deployment workflow.'
    ]
  },
  {
    version: 'v0.8.2.119',
    date: '2026-03-03',
    title: 'Login Screen Omnipresence & 3CX Fixes',
    changes: [
      'Fix CRITICAL: 3CX Webhook crashing with `500 user_id constraint` during insert. Now safely falls back to `user_profiles`.',
      'Fix: Live AI Assist and 3CX Dialer incorrectly rendering on the public login page. They now require strict authentication.',
      'Feature: Live AI Assist panel now has a minimize button to tuck it away when not in active use.',
    ]
  },
  {
    version: 'v0.8.2.118',
    date: '2026-03-03',
    title: 'Pax Portal Complete UI Polish & Banners',
    changes: [
      'Feature: Pax Dashboard Banner Carousel with auto-rotating promotions',
      'Feature: Admin Interface for managing marketing banners',
      'Feature: Consumer-grade Glassmorphism UI for Award Search, Flight Search, FLIFO, and Seat Maps',
      'Fix: Resolved strict TypeScript schema issues for Availability Alerts and FlightStats ratings',
    ]
  },
  {
    version: 'v0.8.2.117',
    date: '2026-03-02',
    title: 'Unified Communication Center (Phase 4 AI Copilot)',
    changes: [
      'Feature: AI Copilot - Natural language command execution in the Communication Hub',
      'Feature: Automations UI - Build rules and canned responses for tickets',
      'Feature: Bulk Actions - Close, Assign, and Merge tickets instantly via dashboard',
      'Feature: Live Knowledge Base Sync - Auto-ingest closed tickets for AI context sharing',
      'Infra: fd-webhook-listener and crm-to-fd-sync fully migrated to comm_tickets schema',
    ]
  },
  {
    version: 'v0.8.2.116',
    date: '2026-02-25',
    title: 'PNR Record Showcase Mode',
    changes: [
      'Feature: PNR Record page header — passenger names as LastName/Initial, total fare as en-dash in Presentation Mode',
    ]
  },
  {
    version: 'v0.8.2.115',
    date: '2026-02-25',
    title: 'Platform-Wide Presentation Mode',
    changes: [
      'Feature: Showcase mode extended to PNR Record (PricingCard, EditablePricingCard, AccountingCard, PassengersCard, ClientPricingSummary)',
      'Feature: PNR Manager page — names as LastName/Initial, fares as en-dash',
      'Audit: 20+ components now respect Presentation Mode across Dashboard, PNR, CRM, Finance, PNT, Revenue, Suppliers, and Analytics',
    ]
  },
  {
    version: 'v0.8.2.114',
    date: '2026-02-25',
    title: 'Dashboard Presentation Mode Hardening',
    changes: [
      'Feature: Dashboard showcase mode — all 7 widgets now respect Presentation Mode with natural, professional formatting',
      'Names display as LastName/Initial (airline-style); financial figures show as en-dash; subjects and alerts remain visible for operational context',
      'Widgets: QuickStatsBar, RecentPNRsWidget, PNTUrgencyWidget, NeedingAttentionWidget, ActionRequiredWidget, UpcomingTravelWidget, RiskAlertsWidget',
    ]
  },
  {
    version: 'v0.8.2.113',
    date: '2026-02-25',
    title: 'PNR Record Crash Fix & Presentation Mode',
    changes: [
      'Fix CRITICAL: PNR Record Page crash (TypeError: undefined reading S) — caused by @react-three/fiber v9 accessing React 19 internals on React 18; downgraded to v8',
      'Fix: Presentation Mode toggle not responding — added missing preferences JSONB column to user_profiles table',
      'Chore: Disabled production sourcemaps for smaller bundle sizes',
    ]
  },
  {
    version: 'v0.8.2.112',
    date: '2026-02-25',
    title: 'PNR Data Integrity & Tools',
    changes: [
      'Feature: Visa Requirements Check - Added OpenRouter-powered edge function to verify passport/visa needs for international itineraries',
      'Feature: Empty Duplicate Archiving - Duplicate PNR warnings now detect if the copy is empty/cancelled and offer a 1-click Archive button',
      'Fix: Missing PNR Elements - Tickets (TE), Price Quotes (PQ), and Accounting Lines (AC) now properly parsed and rendered in UI',
      'Fix: FD Ticket Name Parsing - Auto-detects and extracts "*FDxyz" from name fields for Freshdesk enrichment',
    ]
  },
  {
    version: 'v0.8.2.110',
    date: '2026-02-25',
    title: 'PNR Record Data Display Fix',
    changes: [
      'Fix CRITICAL: PNR record page showed em-dashes for all fields — loadRecord set state to raw Supabase array instead of first element (data vs data[0])',
      'Fix: Archive PNR error (404) — PostgREST schema cache was stale after migration, reloaded via NOTIFY pgrst',
    ]
  },
  {
    version: 'v0.8.2.109',
    date: '2026-02-24',
    title: 'PNR Currency Error Fix (Root Cause)',
    changes: [
      'Fix CRITICAL: EditablePricingCard crash — pricingOverride !== null failed to guard against undefined (undefined !== null is true in strict equality). Changed to != null (loose equality) to catch both null and undefined',
      'Hardened PNRRecordPage pricing useMemo to validate rawPricing has currency before returning',
    ]
  },
  {
    version: 'v0.8.2.108',
    date: '2026-02-24',
    title: 'PNT Phase 3: Architectural Hardening',
    changes: [
      'PNT Table Virtualization: Replaced static HTML table with react-virtuoso TableVirtuoso for smooth scrolling on large datasets',
      'PNT Realtime Subscriptions: Added Supabase Realtime channel for live pnt_transactions updates (INSERT/UPDATE/DELETE auto-refresh)',
      'Orphaned Payment Sweeps: Added pg_cron job (daily 2:30 AM) to flag Open transactions with no PNR older than 30 days',
      'DB Migration: Deployed flag_orphaned_pnt_payments() function and cron schedule to Supabase',
    ]
  },
  {
    version: 'v0.8.2.106',
    date: '2026-02-24',
    title: 'PNR Currency Error Fix',
    changes: [
      'Fix CRITICAL: PNR Record Page - Added safeguards to prevent "Cannot read properties of undefined (reading \'currency\')" when pricing arrays or objects are missing in parsed PNR data',
    ]
  },
  {
    version: 'v0.8.2.102',
    date: '2026-02-23',
    title: 'PNT Enhancements',
    changes: [
      'Feature: PNT Statuses - Added "Service Fee" and "Research" as terminal statuses affecting realized profit',
      'UI: PNT List - Tightened layout for timeline and liability summary boxes for better high-density viewing'
    ]
  },
  {
    version: 'v0.8.2.101',
    date: '2026-02-21',
    title: 'Amadeus & PNR UI Enhancements',
    changes: [
      'Feature: PNR Routing - Users can now route to PNR pages directly via 6-character locators',
      'Feature: Manage My Booking - Added direct MMB links for supported carriers with click-to-copy fields',
      'Feature: Credit Card Detection - FOP badges identify if cards are Customer vs Agency-owned',
      'Refactor: Trip Creation - Standardized Trip Name generation (DD-DD Month YYYY) and RLS-compliant creation',
      'Fix: Amadeus Parser - Resurrected missing pricing, taxes, and ticket elements in parsed payload'
    ]
  },
  {
    version: 'v0.8.2.100',
    date: '2026-02-21',
    title: 'Phase 2 & 3: Ticket Hierarchy & Third-Party Apps',
    changes: [
      'Feature: Hierarchical Ticketing - Database schema support for parent/child ticket relationships',
      'Feature: Ticket Linking UI - New TicketHierarchy widget to view, link, and unlink parent/child tickets directly from the sidebar',
      'Feature: Calendly Integration - Native scheduling widget in sidebar using `react-calendly` to securely book meetings with requesters',
      'Feature: SLA Hardening - Postgres `calculate_sla_target` function and `comm_business_hours` table added for business-hour aware SLA limits'
    ]
  },
  {
    version: 'v0.8.2.99',
    date: '2026-02-21',
    title: 'Phase 1: Automation & Routing Engine',
    changes: [
      'Feature: Automation Webhooks - New `comm-automation-engine` Edge Function securely triggering on ticket inserts/updates',
      'Feature: Automation Routing - Rules engine supporting agent/group assignment, tags, and priority modification',
      'Feature: External Integrations - Webhook execution for third-party bots (e.g., tasklet.ai)',
      'Feature: Time-Triggered Sweeper - Scheduled `ticket-lifecycle-worker` via pg_cron to catch breaching SLA/thresholds',
    ]
  },
  {
    version: 'v0.8.2.98',
    date: '2026-02-20',
    title: 'VNC Startup Stability Fixes',
    changes: [
      'Fix: VM Manager - Explicitly wait for Xvfb and x11vnc to start in vm-desktop-service',
      'Fix: VM Desktop Service - Fly.io per-machine routing and WebSocket connection resets',
      'Fix: vm-manager edge function - Explicitly parse BROWSER_SERVICE_URL and improve startup error logging',
    ]
  },
  {
    version: 'v0.8.2.97',
    date: '2026-02-20',
    title: 'Presentation Mode',
    changes: [
      'Feature: Presentation Mode - Added global toggle to conceal sensitive financial and customer data',
      'Security: Deactivation requires password protection',
      'UI: Obfuscated margins, agent payouts, supplier commissions, and full passenger names',
    ]
  },
  {
    version: 'v0.8.2.95',
    date: '2026-02-18',
    title: 'Amadeus Agent Route Fix',
    changes: [
      'Fix: Added missing /amadeus/agent route to router configuration',
      'Verified: Amadeus Availability, Terminal, and standard routes are accessible',
    ]
  },
  {
    version: 'v0.8.2.96',
    date: '2026-02-19',
    title: 'PropLet VNC Security Fix',
    changes: [
      'Fix: CSP Policy - Whitelisted fly.dev domains to allow PropLet VM VNC streaming',
    ]
  },
  {
    version: 'v0.8.2.94',
    date: '2026-02-18',
    title: 'Amadeus Parity - Phase 4',
    changes: [
      'Feature: Amadeus Availability - Search, Book, and PNR Creation via Amadeus REST/Cryptic APIs',
      'Feature: Amadeus Fare Rules - Structured rule display via Fare Gateway',
      'Feature: Amadeus Ancillaries - Service catalog and cart management',
      'Feature: Amadeus AI Agent - Context-aware terminal agent',
      'Infra: Router Updates - Added lazy-loaded routes for all Amadeus components (Availability, PNR, Rules, Ancillary, Agent)',
    ]
  },
  {
    version: 'v0.8.2.93',
    date: '2026-02-18',
    title: 'PNT Stripe Refund Integration',
    changes: [
      'Fix CRITICAL: Stripe Webhook Secret Naming - Corrected environment variable names to enable webhook event processing',
      'Feature: Refund Polling - Added proactive refund discovery to stripe-sync-pnt using stripe.refunds.list()',
      'Feature: Charge ID Priority Lookup - Enhanced "Mark as Refunded" with 3-tier lookup (charge_id → payment_intent → email+amount)',
      'Deployed: stripe-webhook-pnt and stripe-sync-pnt edge functions',
    ]
  },
  {
    version: 'v0.8.2.92',
    date: '2026-02-17',
    title: 'PNR Record Fix',
    changes: [
      'Fix: PNR Record Page - Resolved "ReferenceError: Link is not defined" crash',
    ]
  },
  {
    version: 'v0.8.2.91',
    date: '2026-02-16',
    title: 'VM Manager Fix & Edge Function Hardening',
    changes: [
      'Fix: VM Manager - Direct Supabase queries for list/create (bypasses broken edge function)',
      'Fix: vm-manager & vm-control-plane edge functions - Replaced jsr: import with esm.sh (fixes 500 boot failures)',
      'Fix: VM assignment column name mismatch (assigned_proplet_id → assigned_tasklet_id)',
    ]
  },
  {
    version: 'v0.8.2.90',
    date: '2026-02-16',
    title: 'PropLets Loading Fix',
    changes: [
      'Fix: PropLets page - Replaced unreliable edge function call with direct Supabase query for listing agents',
      'Fix: PropLet create flow - Removed orphan setShowCreateModal reference that would crash quick-create',
    ]
  },
  {
    version: 'v0.8.2.89',
    date: '2026-02-16',
    title: 'Version Display Fix & Agent Runner v2',
    changes: [
      'Fix: Version Display - Removed duplicate v prefix from LoginPage and RTWPlannerPage (was showing vv0.8.2.88)',
      'Fix: Agent Runner - Deployed to new slug agent-runner-v2 (original slug corrupted on Supabase platform)',
      'Fix: SQL invoke_agent_runner() - Rewrote using pg_net (net.http_post) instead of missing http extension',
    ]
  },
  {
    version: 'v0.8.2.88',
    date: '2026-02-15',
    title: 'Agent Runner Fix & PropLet Schema',
    changes: [
      'Fix: Agent Runner - Removed croner dependency causing 503 boot failures, replaced with built-in cron calculator',
      'Fix: PropLet Schema - Renamed ai_tasklets/triggers/executions tables to ai_proplets equivalents',
      'Fix: Permissions - Added admin access for /ai/* and /agent/* routes',
      'Fix: Background Scheduler - Hardcoded credentials into invoke_agent_runner for pg_cron compatibility',
    ]
  },
  {
    version: 'v0.8.2.87',
    date: '2026-02-15',
    title: 'Secrets Vault UX Fix',
    changes: [
      'Fix: Replaced browser confirmation with custom Modal for Secret Deletion to ensure reliable behavior across all browsers/sessions',
    ]
  },
  {
    version: 'v0.8.2.86',
    date: '2026-02-15',
    title: 'Security & Background Execution (Phase 6)',
    changes: [
      'Feature: Secrets Vault - Secure credential storage with UI and Encrypted Edge Function',
      'Feature: Agent Runner - Background execution engine for scheduled Proplets (pg_cron)',
      'Security: Secret Injection - Proplets now securely access user secrets at runtime via proplet-executor',
      'Refactor: "Tasklet" terminology fully replaced with "PropLet" across the platform'
    ]
  },
  {
    version: 'v0.8.2.84',
    date: '2026-02-11',
    title: 'CRM Fix - Customer Detail Page',
    changes: [
      'Fix: Customer Detail Page - Resolved "ReferenceError: RefreshCw is not defined" crash by adding missing icon import',
    ]
  },
  {
    version: 'v0.8.2.83',
    date: '2026-02-11',
    title: 'Payment Method Fix & CRM Cleanup',
    changes: [
      'Fix: Payment Methods - Resolved "catch is not a function" error preventing new card setup',
      'Refactor: Customer Detail Page - Cleaned up unused imports and improved type safety',
      'Stability: Added missing prop passing for Related Persons handlers to prevent runtime errors'
    ]
  },
  {
    date: '2026-02-11',
    title: 'Related Persons Management',
    changes: [
      'Added Edit/Delete actions for Related Persons',
      'Implemented Retroactive Linking (Edit -> Link Existing Customer)',
      'Added confirmation dialog for deletion'
    ]
  },
  {
    version: 'v0.8.2.81',
    date: '2026-02-11',
    title: 'Smart Reciprocal Linking',
    changes: [
      'Added bi-directional linking for Related Persons',
      'Implemented auto-suggestion for reciprocal relationships',
      'Updated Add Person modal UI'
    ]
  },
  {
    version: 'v0.8.2.80',
    date: '2026-02-11',
    title: 'CRM Fixes',
    changes: [
      'Fixed "Add Related Person" modal not appearing on click',
    ]
  },
  {
    version: 'v0.8.2.79',
    date: '2026-02-11',
    title: 'CRM Cross-Linking',
    changes: [
      'Added ability to link Related Persons to existing CRM Customers',
      'Enhanced Add Related Person modal with customer search',
      'Added navigation link to related customer profiles',
      'Improved accessibility for form inputs'
    ]
  },
  {
    version: 'v0.8.2.78',
    date: '2026-02-10',
    title: 'CRM Enhancements - Related Persons & Responsive Tabs',
    changes: [
      'Feature: Related Persons - Added ability to manage related persons (e.g., family, spouse) in Customer Profile',
      'UI: CRM Tabs - Implemented responsive design for CRM tabs to wrap gracefully on smaller screens',
      'Database: Added related_persons table with RLS policies',
    ],
  },
  {
    version: 'v0.8.2.77',
    date: '2026-02-10',
    title: 'Ticket Lifecycle Worker - pg_cron Scheduling',
    changes: [
      'Feature: Automated ticket lifecycle processing — pg_cron sweeps open tickets every 5 minutes against time-based automation rules',
      'Fix: Edge function schema alignment — corrected column references (is_active→active, priority→position) preventing silent query failures',
      'Feature: Batch sweep mode — ticket-lifecycle-worker now supports scheduled batch processing alongside event-driven single-ticket mode',
    ],
  },
  {
    version: 'v0.8.2.76',
    date: '2026-02-09',
    title: 'Freshdesk Parity - Admin Settings & Sync',
    changes: [
      'Feature: Admin Interface - Added Communications Settings page with Status Editor (CRUD) for dynamic status management',
      'Feature: Dashboard Sync - Enhanced "Sync" button with configurable date range to trigger backfill import',
      'Feature: Dynamic Statuses - Dashboard now consumes `comm_ticket_statuses` for live status badges and filtering',
    ],
  },
  {
    version: 'v0.8.2.75',
    date: '2026-02-09',
    title: 'Dashboard Stability Fix',
    changes: [
      'Fix CRITICAL: Resolved 400 Bad Request error in Dashboard stats caused by invalid column reference (first_responded_at -> first_response_time)',
    ],
  },
  {
    version: 'v0.8.2.74',
    date: '2026-02-09',
    title: 'Deployment Synchronization',
    changes: [
      'Infra: Aligned package.json version with build version to ensure correct deployment tagging',
    ],
  },
  {
    version: 'v0.8.2.73',
    date: '2026-02-09',
    title: 'Dashboard UX Enhancement',
    changes: [
      'UX: Helper - Dashboard now defaults to "All Open Tickets" view instead of "My Open Tickets" to ensure visibility on load',
    ],
  },
  {
    version: 'v0.8.2.72',
    date: '2026-02-09',
    title: 'Dashboard Schema Alignment',
    changes: [
      'Fix: Schema Mismatch - Aligned dashboard query to use "responder_id" instead of "assignee_id" to match production DB',
      'Refactor: UnifiedCommsDashboard now supports both legacy filter keys for backward compatibility',
      'Fix: Resolved "No conversations found" error caused by missing column exception',
    ],
  },
  {
    version: 'v0.8.2.69',
    date: '2026-02-09',
    title: 'Unified Communications Dashboard Refactor',
    changes: [
      'Feature: Dashboard Overhaul - Removed legacy Freshdesk integration in favor of direct Supabase "comm_ticket_views"',
      'Feature: Dynamic View Selector - Added configurable views (My Open, All Unassigned, etc.) to dashboard header',
      'Feature: Card Layout - Replaced table rows with detailed ticket cards (Subject, Preview, Requestor, Tags)',
      'Feature: SLA Indicators - Added visual "Due in X hours" / "Overdue" badges to ticket cards',
      'Feature: Bulk Actions - Added selection/bulk tools for efficient ticket management',
    ],
  },
  {
    version: 'v0.8.2.68',
    date: '2026-02-08',
    title: 'Trip Management & Multi-PNR Linking',
    changes: [
      'Feature: Trip Management Card — Link multiple PNRs together under a shared Trip ID',
      'Feature: Create new trips, link existing trips, view sibling PNRs',
      'Feature: "Copy to RTW" now works seamlessly with linked trips',
      'Database: Added trip_id to pnr_records table for persistent grouping',
      'Fix: Resolved UUID mismatch error when linking PNRs (ensured consistent UUID usage)',
    ],
  },
  {
    version: 'v0.8.2.67',
    date: '2026-02-08',
    title: 'PNR Record Page — 9-Bug Fix Batch',
    changes: [
      'Fix: RemarksCard extractText — empty string text no longer stringified as "undefined"',
      'Fix: RTW Carrier Pricing Panel — no longer blocked by early return for non-RTW PNRs',
      'Fix: Aircraft type display — aircraftTypeName fallback in segment mapping',
      'Fix: Contact sync toast — warning instead of error when Sabre update fails (local save succeeds)',
      'Fix: FF numbers — now enriched from per-passenger ffps and CRM loyalty data',
      'Fix: Freshdesk auto-detect — pattern scan for ticket IDs in PNR names and remarks',
      'Fix: Action Checklist — Freshdesk ticket/contact IDs shown as clickable links',
      'Fix: Passport checker — relaxed to only require passport number (defaults for expiry/country)',
    ],
  },
  {
    version: 'v0.8.2.66',
    date: '2026-02-08',
    title: 'PNR Record Page — 6-Phase Overhaul',
    changes: [
      'Feature: SegmentsTable rebuilt — airline logos (pics.avs.io), split carrier/flight columns, operating carrier indicator, date/time formatting, status color badges, aircraft tooltips',
      'Feature: RemarksCard rebuilt — structured SSR/OSI/Remark parsing with color-coded code badges, collapsible sections, count badges, SSR code explanations (80+ codes)',
      'Feature: Mini Sabre Terminal — inline dark-themed terminal for Sabre PNRs with auto-PNR display, quick-action buttons (*PDN, *PDQ, WPQ, *S*), command history (↑↓), Refresh PNR',
      'Feature: RTW Carrier Pricing toggle — available on all PNRs (default off), auto-shows for RTW+MKUP type PNRs',
      'Fix: Summary card — status capitalized, all passengers listed with ★ lead indicator, departure date formatted DD MMM YY',
      'Fix: CRM sync — fallback to local save when Sabre API update fails (warning toast instead of error)',
      'Fix: Match Customer button — shows "Re-match" with amber styling when customer already linked',
    ],
  },
  {
    title: 'FlightStats / Cirium Flex API Integration + Fab 4 Codeshare Suggestions',
    changes: [
      'Feature: Full FlightStats Flex API integration — 12 endpoints via flightstats-proxy edge function',
      'Feature: Fab 4 Codeshare Suggestions — auto-discovers BA/AA/IB/AY codeshares for 2.5x Finnair TP earning in RTW Planner',
      'Feature: Codeshare Discovery Service — batch lookup with per-segment tier point improvement calculation',
      'Feature: FlightStats Intelligence panel on FLIFO — airport delay index (0-5), route on-time %, detailed codeshare partners',
      'Feature: Segment Marketplace now queries FlightStats codeshares alongside Sabre and Duffel',
      'Feature: Connection Quality Service — evaluates layover risk using route ratings + delay index',
      'Feature: Flight Enrichment enhanced with FlightStats codeshare data and departure delay index',
      'Feature: Disruption Management proactive warnings when departure airport delay index > 3',
      'Feature: Variable TTL caching — codeshare 30m, delay_index 2m, route_ratings 24h, equipment 7d',
      'Fix: Replaced hardcoded AA codeshare suggestion in tier calculator with actual FlightStats data',
    ],
  },
  {
    version: 'v0.8.2.64',
    date: '2026-02-08',
    title: 'RTW Planner — Stopover Auto-Detection Fix',
    changes: [
      'Fix: Stopovers now auto-detected from departure/arrival time gaps (>=24h) across all import paths',
      'New: Shared stopoverDetector.ts utility — single source of truth for stopover calculation',
      'Fix: Sabre API import and PNR sync now compute stopovers on import',
      'Fix: useEffect-based auto-recalculation when segment times change (manual edits, reorder, etc.)',
    ],
  },
  {
    version: 'v0.8.2.63',
    date: '2026-02-08',
    title: 'RTW Planner — 7 Issue Fix',
    changes: [
      'Fix: Fare Rules Validation now detects fare type (DONE3-DONE6, DGLOB34) and shows specific type instead of Unknown',
      'Fix: Stopover auto-detection from departure/arrival time gaps (>24h = stopover)',
      'Fix: Backtracking/direction warnings — excluded closing leg, 60% threshold, RTW-valid downgrades to info',
      'Fix: Right panel overflow — EnhancedTierPointsDisplay cards stacked (grid-cols-1), right column max-w-full',
      'Fix: Branded logos added to summary card headers (Finnair Plus, BA Executive Club, QR Privilege Club)',
      'Fix: Validating Carrier Optimizer — per-person pricing with total in small text (passengerCount prop)',
      'Fix: StatusTimeline — QR Qpoints column, points badge, running total added; BA progress bar max corrected to 20,000',
      'Lint: Cleaned all warnings — removed unused imports/vars, fixed any types, proper eslint-disable for casts',
      'Type: Added "info" to RuleStatus union type for soft RTW validation warnings',
    ],
  },
  {
    version: 'v0.8.2.62',
    date: '2026-02-08',
    title: 'Access Denied Race Condition Fix',
    changes: [
      'Fix: Transient "Access Denied" flash on page load for super_admin users caused by auth check race condition',
      'Added retry logic (3 attempts, 500ms delay) to ProtectedRoute auth checks while user profile loads',
      'Fix: GDS Settings page crash - added missing React useState/useEffect imports',
      'Applied same retry fix to PropellerProtectedRoute for consistency',
    ],
  },
  {
    version: 'v0.8.2.61',
    date: '2026-02-08',
    changes: [
      'Fix CRITICAL: useBreadcrumb Infinite Loop - Memoized setBreadcrumb/clearBreadcrumb with useCallback to produce stable references',
      'Fix: Resolves React error #185 (Maximum update depth exceeded) on PNR Manager, All Trips, Customers, and Invoices pages',
      'Stability: Used useRef for options to avoid recreating callbacks while always reading latest values',
    ],
  },
  {
    version: 'v0.8.2.60',
    date: '2026-02-08',
    changes: [
      'Fix CRITICAL: RTW Planner Infinite Loop - Resolved "Maximum update depth exceeded" error caused by PNR import state mismanagement',
      'Refactor: Replaced window.history.replaceState with proper React Router navigation to ensure state synchronization',
      'Stability: Added reference guard to prevent duplicate PNR processing during component mounting',
    ],
  },
  {
    version: 'v0.8.2.59',
    date: '2026-02-08',
    changes: [
      'Feature: Context Ingestion - Added `/analyze` command to Sabre Agent for call transcript and note analysis',
      'Feature: GDS Command Rules - Added Admin UI for defining mandatory GDS commands (Command Rules tab)',
      'Feature: Policy Enforcement - RTW Planner now automatically appends mandatory commands based on trigger type',
    ],
  },
  {
    version: 'v0.8.2.58',
    date: '2026-02-07',
    changes: [
      'Fix: Unknown Airports (SAF) - Implemented fallback logic in airportService to correctly handle airports like SAF (Santa Fe) even when database continent data is missing',
      'Fix: RTW Validation (Backtracking) - Relaxed backtracking rules to align with Oneworld Explorer rules: ignoring intra-continent direction changes and allowing return to origin continent without penalty',
      'Fix: BA Status Calculation - Updated Gold threshold to 20,000 TP to match 2025 revenue-based system',
    ],
  },
  {
    version: 'v0.8.2.57',
    date: '2026-02-07',
    changes: [
      'Feature: RTW Planner - Added Total Duration and Distance display to EnhancedTierPointsDisplay',
      'Feature: RTW Planner - Added CSV Export functionality for itinerary segments',
      'Performance: RTW Planner - Implemented React.memo for MultiViewRouteMap to reduce re-renders',
      'Stability: RTW Planner - Added Error Boundaries to critical components',
      'Cleanup: RTW Planner - Removed unused imports and optimized code',
    ],
  },
  {
    version: 'v0.8.2.56',
    date: '2026-02-07',
    changes: [
      'Fix: Aircraft Type Missing on Sabre PNRs - Aligned field names between sabre-get-reservation edge function (aircraft) and frontend parsers (aircraftTypeName)',
      'Fix: PNR Manager now reads aircraftTypeName, aircraft, and aircraftTypeCode fallbacks',
      'Fix: RTW Planner importSabrePNR/syncSabrePNR now correctly reads aircraft field from sabre-get-reservation',
      'Fix: sabre-get-reservation edge function now outputs aircraftTypeName alongside aircraft for compatibility',
    ],
  },
  {
    version: 'v0.8.2.41',
    date: '2026-02-04',
    changes: [
      'Debug: Sabre Availability & Fare Rules - Added comprehensive debug logging to diagnose "No flights/fares found" errors',
      'Fix: Sabre Availability - Resolved accessibility lints for form inputs',
      'Refactor: Sabre Fare Rules - Improved error handling for empty search results',
    ],
  },
  {
    version: 'v0.8.2.40',
    date: '2026-02-03',
    changes: [
      'Fix: Communications Dashboard - Added "Closed" status option to filter dropdown',
      'Fix: "All Status" filter now correctly fetches tickets of any status without restriction',
      'Fix: Removed unused imports to improve code quality',
    ],
  },
  {
    version: 'v0.8.2.39',
    date: '2026-02-03',
    changes: [
      'Fix CRITICAL: Sabre Queue Parsing - Added stateful parsing logic to handle "Split Line" PNRs (PNR on one line, Name on next)',
      'Fix: Resolves issue where valid alphabetic PNRs (e.g. QIIXAA) were visible in raw response but missing from UI',
      'Refactor: Improved orphan detection for PNRs without associated names',
    ],
  },
  {
    version: 'v0.8.2.38',
    date: '2026-02-03',
    changes: [
      'Fix CRITICAL: Sabre PNR Validation - Enforced STRICT 6-letter alphabetic format (no numbers allowed) per user confirmation',
      'Fix: Reverted previous alphanumeric regexes that were incorrectly vetting numeric PNRs',
      'Debug: Added debug_raw_response to gateway output to allow frontend inspection of raw GDS data',
    ],
  },
  {
    version: 'v0.8.2.37',
    date: '2026-02-03',
    changes: [
      'Fix CRITICAL: Queue Item Parsing - QueueDetailViewEnhanced was using old sabre-queue-access function instead of fixed sabre-queue-gateway',
      'Fix: Queue gateway action parameter now correctly set to "access" for proper routing',
      'Fix: Added diagnostic logging to gateway to capture raw Sabre response format',
      'Cleanup: Removed garbage PNRs from database (city pairs like EGIJFK, JFKDOH)',
    ],
  },
  {
    version: 'v0.8.2.36',
    date: '2026-02-03',
    changes: [
      'Feature: Helpdesk Ticket Merging - Manual merge UI and automated duplicate detection by sender/subject',
      'Feature: Agent Signatures - Added signature field to comm_agents with automatic append in replies',
      'Feature: Email Tracking - Tracking pixel implementation with read status indicators in conversation thread',
      'Feature: 3CX Phone Integration - HTTP endpoint for caller ID lookup with XML template for import',
      'Feature: Canned Responses - Searchable modal selector with folder organization and content insertion',
      'Database: Ticket merge function merge_tickets(source_id, target_id) for conversation consolidation',
      'Database: Email tracking columns (opened_at, tracking_id) in comm_conversations table',
      'Database: Canned response tables (comm_canned_folders, comm_canned_responses) populated from Freshdesk',
      'UI: CannedResponseSelector component with search and folder navigation',
      'UI: ConversationMessage updated to show Read/Unread status for agent messages',
      'Integration: Auto-merge logic in ticket-ingest Edge Function',
    ],
  },
  {
    version: 'v0.8.2.35',
    date: '2026-02-02',
    changes: [
      'Infra: Platform Core structure - @pt/core, @pt/integrations, @pt/ai packages with canonical types',
      'Infra: Unified PNR parser (parsePNR) with auto-detection for Sabre/Amadeus formats',
      'Infra: Build metadata injection - version, timestamp, git SHA at build time',
      'Infra: Enhanced VersionDisplay with changelog modal (react-markdown)',
      'Infra: Automation Glassbox framework - generic run tracking with real-time log streaming',
      'Infra: useAutomationRealtime hook + AutomationGlassbox component',
      'Infra: withAutomationRun helper for edge functions',
      'Infra: ESLint boundary rules to encourage @pt/* package usage',
      'CI: Changelog check workflow for PRs',
    ],
  },
  {
    version: 'v0.8.2.34',
    date: '2026-02-02',
    changes: [
      'Fix CRITICAL: Sabre Queue Garbage Collection - Architectural fix to expose alphanumeric garbage items from gateway to auto-ingester',
      'Fix: sabre-queue-gateway now returns both valid PNRs and invalidItems (garbage) in response',
      'Fix: sabre-queue-auto-ingest now consumes invalidItems and triggers purgeQueueItems to remove garbage from Sabre',
      'Fix: Queues stuck with alphanumeric noise (PJ1E04, LONDON, etc.) will now be properly cleared',
    ],
  },
  {
    version: 'v0.8.2.33',
    date: '2026-02-01',
    changes: [
      'Fix: Removed Scenario Planner and Cash Flow Projection chart from Cash Flow Center per user request',
      'Fix: Stripe Financials Widget - Corrected environment variable key order (STRIPE_UK_SECRET_KEY → STRIPE_SECRET_KEY_UK)',
      'Fix: Supplier Liability RPC - Fixed type mismatch error (0::numeric cast for pay_total)',
      'Fix: Added missing @headlessui/react dependency causing Vercel build failures',
      'Feature: Updated Cash Flow Center to strict live-data-only display',
    ],
  },
  {
    version: 'v0.8.2.32',
    date: '2026-02-01',
    changes: [
      'Feature: Cash Flow Phase 2 - Stripe Multi-Account Support (UK/US)',
      'Feature: Stripe Audit Modal - Click any balance to see last 20 real transactions from ledger',
      'Feature: Supplier Liability Breakdown Modal - Strict reconciliation (Statement + Activity) per supplier',
      'Feature: Live Cash Enhanced Metadata - True bank names (Mercury, Revolut) with distinct Live API tags',
      'Backend: Updated get-stripe-live-balance Edge Function for multi-account (STRIPE_SECRET_KEY_UK/US)',
      'Backend: DB Migration supplier_statements table + get_supplier_reconciliation_summary RPC',
      'UI: Removed all mock data - strict enforcement of verified live data only',
    ],
  },
  {
    version: 'v0.8.2.31',
    date: '2026-02-01',
    changes: [
      'Feature: Cash Flow Center - Complete overhaul of Cash Flow dashboard with live banking integration',
      'Feature: Live Cash Widget - Real-time "Cash on Hand" display with "Live API" vs "Manual" freshness indicators',
      'Feature: Stripe Financials Widget - Live Available/Pending balances, Reserves, and Payout schedulde via Edge Function',
      'Feature: Supplier Liability Widget - Aggregated open balances and commission receivables',
      'Feature: Recurring Payment Detection - AI-powered detection of recurring transactions in manual entry modal',
      'Backend: Edge Function "get-stripe-live-balance" for secure realtime financial data',
      'Backend: DB Migration "add_bank_account_meta" for tracking data source and freshness',
    ],
  },
  {
    version: 'v0.8.2.30',
    date: '2026-01-31',
    changes: [
      'Feature: Amadeus Environment Switcher - Added Production/Test environment selector to Queue Manager and Terminal',
      'Feature: Amadeus Authentication - Updated credential retrieval to support environment-specific credentials (Test/Production)',
      'Fix: Session Management - Terminal session is now cleared when switching environments to prevent authorization errors',
    ],
  },
  {
    version: 'v0.8.2.29',
    date: '2026-01-30',
    changes: [
      'Fix: PNR Fetch Parameter - fetchFullPNR now sends confirmationId (not recordLocator) to sabre-get-reservation',
      'Fix: Version Display - getVersionString now shows timestamp (e.g., v0.8.2.29/30JAN2350UK)',
    ],
  },
  {
    version: 'v0.8.2.28',
    date: '2026-01-30',
    changes: [
      'Fix: PNR Fetch Auth - fetchFullPNR now uses supabaseAuth client to pass user token to sabre-get-reservation',
      'Fix: Resolves "Failed to fetch PNR: Unauthorized" error during queue ingestion',
    ],
  },
  {
    version: 'v0.8.2.27',
    date: '2026-01-30',
    changes: [
      'Fix: Sabre Queue Gateway - Fixed ".catch is not a function" error by replacing .catch() chains with try/catch blocks',
      'Fix: Supabase client Promise pattern compatibility in queue action logging',
    ],
  },
  {
    version: 'v0.8.2.26',
    date: '2026-01-30',
    changes: [
      'Fix: Queue Ingestion Auth - pollQueue now uses supabaseAuth client to pass user token to sabre-queue-gateway',
      'Fix: Resolves "Unauthorized" error when ingesting from queues',
    ],
  },
  {
    version: 'v0.8.2.25',
    date: '2026-01-30',
    changes: [
      'Fix: Queue Ingestion ON CONFLICT - Applied DB migration to replace COALESCE-based index with proper unique constraint (user_id,queue_number,category,pcc)',
      'Fix: Added pcc field to default configs for proper constraint matching',
    ],
  },
  {
    version: 'v0.8.2.24',
    date: '2026-01-30',
    changes: [
      'Fix: Queue Ingestion ON CONFLICT Error - Fixed "no unique or exclusion constraint" error in Sabre queue ingestion by correcting the upsert conflict columns to match actual DB index (user_id,queue_number,category)',
      'Fix: PNR Customer Linking - Fixed linkCustomerToPNR to use correct columns (record_locator,gds_source,passenger_key) instead of non-existent pnr_id',
      'Fix: Realtime Subscription Spam - Prevented hundreds of CLOSED messages by using refs for callbacks in usePNRIngestionRealtime',
    ],
  },
  {
    version: 'v0.8.2.23',
    date: '2026-01-30',
    changes: [
      'Fix: PNT List Table Overlap - Fixed header truncation and overlap issues by implementing strict width controls and overflow handling',
      'Fix: Hardened SortHeader and TableHeader components to prevent layout shifts',
    ],
  },
  {
    version: 'v0.8.2.22',
    date: '2026-01-29',
    changes: [
      'Deploy: Amadeus GDS Integration - Deployed Amadeus SOAP (Cryptic/Queues) and REST (Search) Edge Functions',
      'Deploy: Invoice Intelligence - Deployed supplier invoice matching and auto-reconciliation services',
      'Deploy: PNT OB Points - Deployed DB migration for British Airways On Business points tracking',
      'Deploy: Resolution Suggestions - Deployed suggestion engine for queue management and PNR resolution',
      'Deploy: Real-Time Ingestion - Deployed real-time PNR ingestion logging infrastructure',
    ],
  },
  {
    version: 'v0.8.2.21',
    date: '2026-01-29',
    changes: [
      'Feature: Queue Glass Window Monitor - Real-time terminal-style log display during PNR queue ingestion',
      'Feature: Stop/Pause/Resume Controls - Full control over running ingestion processes via database RPC',
      'Feature: Enhanced PNR Ingestion Pipeline - Now processes ALL PNRs (not just ticketed), assigns lifecycle status',
      'Feature: Retention Segment Auto-Management - Auto-detect and fix missing retention for future-dated PNRs',
      'Feature: Resolution Suggestions Engine - Intelligent AI-powered suggestions with one-click actions',
      'Feature: 7 Suggestion Types - ticketing_deadline, schedule_change, customer_match, missing_retention, fare_expiry, duplicate_detection, ssr_validation',
      'Feature: Priority Ranking Algorithm - VIP bonus, high-value booking bonus, deadline urgency scoring',
      'Feature: Batch Processing Operations - Bulk process, link customer, add retention, dismiss, assign actions',
      'Feature: Queue Analytics Dashboard - Processing trends, match distribution, queue sources, retention compliance',
      'Feature: Queue Alert Toast System - Critical item notifications, expiring retention, ingestion failures',
      'Database: pnr_ingestion_logs table with Supabase realtime enabled for live streaming',
      'Database: resolution_suggestions table with ranking, deadlines, and outcome tracking',
      'Database: suggestion_templates table for configurable suggestion generation',
      'Hooks: usePNRIngestionRealtime, useSuggestions, useIngestionAnalytics',
      'Services: suggestionGeneratorService, suggestionActionHandlers',
      'Components: IngestionGlassMonitor, ResolutionSuggestionsPanel, BatchActionBar, QueueAnalyticsDashboard, QueueAlertToast',
    ],
  },
  {
    version: 'v0.8.2.20',
    date: '2026-01-29',
    changes: [
      'Feature: AOC Mission Control V2 - Complete overhaul of Aviation Operations Center',
      'Fix: Ticketing status bug - API-imported PNRs without PNT records now show "Unknown" not "Un-Ticketed"',
      'Fix: Time windows expanded to -6h/+12h for Departing Now section',
      'Feature: Real-time flight enrichment via FlightAware AeroAPI (delays, gates, terminals, progress)',
      'Feature: Weather Intelligence Layer - Live METAR/TAF data with VFR/MVFR/IFR/LIFR indicators',
      'Feature: Enhanced FLIFO display - Airline-style format (CX113 HKG→AKL 2135/1524+1)',
      'Feature: Risk Assessment Service - Weighted scoring across ticketing, weather, delays, connections',
      'Feature: Disruption Management Service - Analyzes delays, cancellations, missed connections with EU261 eligibility',
      'Feature: Passenger Communication Hub - Notification templates for delays, cancellations, gate changes',
      'Feature: Operations Analytics Dashboard - OTP metrics, supplier performance, ticketing distribution',
      'UI: Weather/delay/cancellation alerts in dashboard header with alert banners',
    ],
  },
  {
    version: 'v0.8.2.19',
    date: '2026-01-29',
    changes: [
      'Feature: OB Points Tracking for PNT - Added support for recording British Airways On Business points in PNT lists',
      'UI: Added OB Points action button to PNT transaction rows',
      'Logic: Profitability now calculates based on Fees Paid when using OB Points',
    ],
  },
  {
    version: 'v0.8.2.18',
    date: '2026-01-29',
    changes: [
      'Fix CRITICAL: AerTicket Balance Reconciliation - Fixed logic to exclude historical statements from new activity (RPC fix)',
      'Fix: Statement Date Parsing - Improved 2-digit year handling for supplier statement files',
      'Verification: Executed balance check confirming correct open balance',
    ],
  },
  {
    version: 'v0.8.2.17',
    date: '2026-01-28',
    changes: [
      'Feature: AOC now monitors raw PNRs directly (Virtual Trips) + existing Trips',
      'Fix: Solved missing active trips in AOC dashboard',
    ],
  },
  {
    version: 'v0.8.2.16',
    date: '2026-01-28',
    changes: [
      'Dashboard: Improved Recent PNRs display for "Today" trips (shows segments & departure time)',
      'Fix: Prevented "Pending" status downgrade for trips departing today',
    ],
  },
  {
    version: 'v0.8.2.15',
    date: '2026-01-28',
    changes: [
      'Fix: AOC Trip Categorization - Added support for "En-Route" flights (active but departed >2h ago)',
      'Debug: Added extensive console logging for AOC trip data triage',
      'UX: Added visible version identifier to AOC header',
    ],
  },
  {
    version: 'v0.8.2.14',
    date: '2026-01-28',
    changes: [
      'Build: Forced deployment trigger to ensure all pending changes are verified',
      'Verification: Checked git status and remote sync state',
    ],
  },
  {
    version: 'v0.8.2.13',
    date: '2026-01-28',
    changes: [
      'Feature: PNR Retention Segment Maintenance - Automated check for missing/expiring retention lines',
      'Sabre: Auto-fix logic (delete old/add new) for detected retention segments',
      'Amadeus: Warning banner with copy-paste retention command',
      'Service: pnrRetentionService implemented with robust regex and object parsing',
    ],
  },
  {
    version: 'v0.8.2.9',
    date: '2026-01-28',
    changes: [
      'Fix: TypeScript Lint Errors - Fixed null index type error in PNT map access',
      'Fix: Added type assertion for metadata.weather_risk property access',
      'Fix: TripItem type mismatch - converted all null fields to undefined',
      'Cleanup: Removed unused isBefore/isAfter imports, changed let to const where appropriate',
    ],
  },
  {
    version: 'v0.8.2.8',
    date: '2026-01-28',
    changes: [
      'Fix CRITICAL: AOC Trip Categorization - Flights at exact time boundaries now properly categorized',
      'Fix: Replaced exclusive time checks (isBefore/isAfter) with inclusive isWithinInterval()',
      'Fix: Departing Now (-2h to +6h), Next 24h, and Upcoming buckets now capture all trips correctly',
      'UX: AOC dashboard now shows accurate counts for active trips',
    ],
  },
  {
    version: 'v0.8.2.1',
    date: '2026-01-24',
    changes: [
      'Frontend: Export Safety Enforcement - Added validate_export_safety RPC calls to all financial exports',
      'BalanceReconciliationReport: CSV export now validates resolution status before proceeding',
      'SupplierPaymentLedger: CSV export now validates resolution status before proceeding',
      'Export Behavior: Exports fail loudly with detailed error messages if unresolved transactions exist',
      'User Experience: No silent skips, no fallback categorization, explicit resolution required',
    ],
  },
  {
    version: 'v0.8.1.9',
    date: '2026-01-24',
    changes: [
      'Database: Owner-Impact Proposals - Track company/owner boundary transactions',
      'Proposal Types: owner_expense_paid_by_company, company_expense_paid_by_owner, owner_draw',
      'Generation Rules: ONLY from explicit receipt text or known account transfers',
      'Anti-Inference: Never infer from merchant type, amount, or category',
    ],
  },
  {
    version: 'v0.8.1.8',
    date: '2026-01-24',
    changes: [
      'Database: Transaction Resolutions - Authoritative confirmation system (single source of truth)',
      'Schema: transaction_resolutions table with UNIQUE constraint (one resolution per transaction)',
      'Endpoint: POST /transaction-resolutions/confirm - only path to create resolutions',
      'Foreign Key: Added FK from category_learnings to transaction_resolutions for full audit trail',
      'Architecture Complete: Four-layer system (categories → learnings → proposals → resolutions)',
    ],
  },
  {
    version: 'v0.8.1.7',
    date: '2026-01-24',
    changes: [
      'Database: Proposal Type Extensions - Added receipt_match and legacy_auto proposal types',
      'Receipt Match: Exact amount + explicit reference/merchant matching for Gmail receipts',
      'Legacy Migration: Historical auto-categorizations become proposals requiring human confirmation',
      'Design: No privilege for legacy proposals - all proposals equal',
    ],
  },
  {
    version: 'v0.8.1.6',
    date: '2026-01-24',
    changes: [
      'Database: Transaction Proposals - AI-generated category suggestions for human review',
      'Schema: transaction_proposals table with JSONB evidence tracking',
      'Design: Proposal-only - never auto-creates resolutions, multiple proposals allowed per transaction',
      'Workflow: Pending → Accepted/Rejected/Superseded with full audit trail',
    ],
  },
  {
    version: 'v0.8.1.5',
    date: '2026-01-24',
    changes: [
      'Database: Category Learning Memory - Stores confirmed human category decisions for proposal-only suggestions',
      'Schema: category_learnings table with merchant/supplier scope types',
      'Design: Memory ONLY - no auto-assignment, no triggers, human confirmation required',
      'Audit: Tracks learned_from_resolution_id for full decision trail',
    ],
  },
  {
    version: 'v0.8.1.4',
    date: '2026-01-24',
    changes: [
      'Database: Hierarchical Categories Infrastructure - Unlimited depth category tree via parent_id',
      'Schema: Categories table supports formal (accounting) and operational (workflow) categorization',
      'Future: Will be referenced by transaction_resolutions for banking transaction classification',
      'Design: No automation, triggers, or inference - categories applied only via human confirmation',
    ],
  },
  {
    version: 'v0.8.1.3',
    date: '2026-01-22',
    changes: [
      'Feature: Knowledge Base Rebuild - Migrated to "Human-Verified" architecture with agent attribution',
      'Feature: Freshdesk Enrichment - Knowledge entries now link to Trips (PNR) and Customers (Email)',
      'Security: AI Guardrails - "knowledge_search" tool enforces "only_approved" filter for agents',
      'UI: Enhanced KB Review Dashboard with filtering and integrated approval workflow',
    ],
  },
  {
    version: 'v0.8.1.2',
    date: '2026-01-22',
    changes: [
      'Feature: AOC Phase 4 MVP - New Read-Only Mission Control Dashboard',
      'Feature: Time-bucketed monitoring (Departing Now/Soon/Upcoming)',
      'Feature: Automated Risk Engine (Unticketed PNT, Tight Connections, Delays)',
      'Performance: Optimized data fetching with useAOCMonitoring hook',
    ],
  },
  {
    version: 'v0.8.1.1',
    date: '2026-01-22',
    changes: [
      'Security: Enforced Auth-Derived User ID in critical Edge Functions (aoc-data-orchestrator, admin actions)',
      'Security: Mandatory Webhook Signature Verification implemented for webhook-handler',
      'Audit: Explicit Audit Logging added for Admin Create/Update User actions and Webhooks',
      'Cleanup: Removed legacy V2 routes and unused UnifiedAIPageV2.tsx',
      'Update: Replaced deprecated OpenAI models with gpt-4o across all functions',
    ],
  },
  {
    version: 'v0.8.1.0',
    date: '2026-01-22',
    changes: [
      'BREAKING: Complete removal of V2 experimental infrastructure (/v2/* routes, src/v2/ directory - 21 files)',
      'Infrastructure: Environment awareness foundation (src/lib/environment.ts - single source of truth)',
      'Feature: Enhanced Staging Banner - Non-dismissible, prominent test environment indicator',
      'Feature: useStagingConfirmation hook - Explicit confirmation for destructive actions in staging',
      'Feature: StagingTestModePanel - Visible TEST MODE indicator for AI/Agent surfaces with execution tracking',
      'Feature: auditService - Centralized audit logging for staging environment with action categorization',
      'Integration: TEST MODE panels added to AI Assistant, PropLet Manager, VM Manager, PNR Agent, PNR Parser',
      'Integration: Staging labels added to Queue Monitoring, Automation Engine, Queue Management',
      'Integration: Staging labels added to Finance Dashboard, AOC, Knowledge Base, Admin Dashboard',
      'Database: staging_audit_log table for comprehensive action tracking in staging',
      'Hardening: All surfaces now staging-aware with appropriate labels and confirmations',
      'Navigation: Verified no dead V2 links or orphan navigation entries',
    ],
  },
  {
    version: 'v0.8.0.9',
    date: '2026-01-22',

    changes: [
      'Fix CRITICAL: Quick Actions crash - Added defensive Array.isArray() guards to QuickActionsBar.tsx and navigationStore.ts',
      'Fix: Prevents r.map is not a function error caused by undefined quickActions from database preferences',
    ],
  },
  {
    version: 'v0.8.0.7',
    date: '2026-01-21',
    changes: [
      'Fix CRITICAL: AOC Page Crash - Added defensive Array.isArray() guards to useAOCRealtimeState.ts to prevent TypeError when API returns undefined',
      'Fix: Prevents r.map is not a function error on AOC dashboard load',
    ],
  },
  {
    version: 'v0.8.0.6',
    date: '2026-01-21',
    changes: [
      'Infrastructure: Staging Environment - Created Supabase branch (drrgbpluqxwzahfgodsx) with isolated database',
      'Feature: Staging Banner - Fixed amber banner displays at top of page when on staging.propellertravel.com',
      'Config: Environment detection via VITE_ENVIRONMENT and hostname check',
    ],
  },
  {
    version: 'v0.8.0.5',
    date: '2026-01-20',
    changes: [
      'Feature: Smart Status Badges - Human-readable labels for 30+ GDS status codes (HK→Confirmed, HN→Waitlisted, etc.)',
      'Feature: Flight Progress Indicators - Past/Current/Next/Upcoming segment states with live countdown',
      'Feature: Final Leg & First Flight badges for segment position awareness',
      'Fix: Removed confusing Unknown status - now shows Pending with helpful tooltip',
      'UX: Segment cards highlight based on progress state',
    ],
  },
  {
    version: 'v0.8.0.0',
    date: '2026-01-20',
    changes: [
      'Feature: Hotel Research Agent - Autonomous AI agent (v0.8.0) capable of researching hotels on Fora.',
      'Infrastructure: VM Service updated with Puppeteer for headless browser automation.',
      'Integration: Full Freshdesk workflow - triggers on "hotel_auto_research" tag, posts Internal Notes and Email Drafts.',
    ],
  },
  {
    version: 'v0.7.8.24',
    date: '2026-01-20',
    changes: [
      'Fix: Pipedream Integration - Resolved "Link Expired" error by whitelisting platform domain in token generation.',
    ],
  },
  {
    version: 'v0.7.8.23',
    date: '2026-01-19',
    changes: [
      'Fix: Dashboard Crash - Resolved ReferenceError (Calendar is not defined) in WidgetSystem by adding missing import.',
    ],
  },
  {
    version: 'v0.7.8.22',
    date: '2026-01-19',
    changes: [
      'Fix: Sabre Enrich - Switched to STRUCTURED API (sabre-get-reservation) instead of parsing cryptic text',
      'Fix: Segments, routing, departure dates, ticket numbers now reliably extracted from REST API response',
      'Fix: No more regex parsing - uses Sabre GetBooking REST API with proper JSON response',
    ],
  },
  {
    version: 'v0.7.8.19',
    date: '2026-01-19',
    changes: [
      'Dashboard: PNR Status Fix - Status is now "Pending" (not Quote) if PNT matches the locator.',
      'Dashboard: Map View - Switched to 2D Mercator projection for better global visibility.',
      'New Widget: Upcoming Travel - Shows departures in next 24h, 3d, 7d.',
    ],
  },
  {
    version: 'v0.7.8.19',
    date: '2026-01-19',
    changes: [
      'Fix: Sabre Enrich now overwrites existing form fields (previously only updated empty fields)',
      'Fix: Ticketed Actuals layout - split into 2 rows (4 cols each) for better spacing',
      'UX: Payment Source + Ticket Numbers + Profit box now in dedicated second row',
    ],
  },
  {
    version: 'v0.7.8.18',
    date: '2026-01-19',
    changes: [
      'Fix: PNT Modal UI V2 - Widened modal to max-w-6xl (1152px), reduced Payment Info to 4 columns',
      'Fix: Payment Source moved from Payment Info to Ticketed (Actuals) section - correctly reflects Propeller Travel payment method, not client payment',
      'UX: Ticketed (Actuals) expanded to 5 columns to accommodate Payment Source',
    ],
  },
  {
    version: 'v0.7.8.17',
    date: '2026-01-19',
    changes: [
      'Fix: PNT Modal UI - Widened modal (max-w-5xl), reduced Booking Info to 4 columns, moved Stripe PI to Payment Info',
      'Fix CRITICAL: Sabre Enrich - Fixed regex quantifier syntax ({1, 2} -> {1,2}) that broke flight segment/date/ticket parsing',
      'Debug: Added comprehensive logging to handleEnrichFromPNR for troubleshooting',
    ],
  },
  {
    version: 'v0.7.8.12',
    date: '2026-01-19',
    changes: [
      'Fix CRITICAL: PNR Manager ReferenceError - Refactored useSabreToken.ts to use import.meta.env.VITE_SUPABASE_URL directly, breaking full circular dependency chain.',
    ],
  },
  {
    version: 'v0.7.8.11',
    date: '2026-01-19',
    changes: [
      'Fix CRITICAL: PNR Manager ReferenceError - Refactored utils/api.ts to Zero-Dependency pattern (uses import.meta.env directly).',
      'Verification: Comprehensive test plan for Sabre integrations, Seat Maps (AA100), and Flight Intelligence APIs.',
    ],
  },
  {
    version: 'v0.7.8.10',
    date: '2026-01-18',
    changes: [
      'Fix CRITICAL: PNR Manager ReferenceError - Removed lingering getEdgeFunctionUrl in EnhancedSegmentsTab to resolve persistent TDZ crash.',
    ],
  },
  {
    version: 'v0.7.8.9',
    date: '2026-01-18',
    changes: [
      'Fix CRITICAL: PNR Manager ReferenceError - Removed getEdgeFunctionUrl helper to eliminate TDZ risk.',
    ],
  },
  {
    version: 'v0.7.8.8',
    date: '2026-01-18',
    changes: [
      'Fix CRITICAL: Resolved ReferenceError ("getEdgeFunctionUrl") in PNRManagerPage caused by TDZ/Circular dependency.',
    ],
  },
  {
    version: 'v0.7.8.7',
    date: '2026-01-18',
    changes: [
      'Hotfix: Resolved infinite render loop (Minified React error #185) by stabilizing DashboardLayout dependencies',
    ],
  },
  {
    version: 'v0.7.8.6',
    date: '2026-01-18',
    changes: [
      'Fix CRITICAL: Resolved ReferenceError in DashboardLayout caused by hook ordering.',
      'Feat: Dynamic page titles enabled.'
    ],
  },
  {
    version: 'v0.7.8.5',
    date: '2026-01-18',
    changes: [
      'Feat: Added dynamic page titles (e.g., "Customer - Propeller Travel Platform").',
      'Fix: Resolved PartnerAttributionCard TypeError preventing Customer Page load.'
    ],
  },
  {
    version: 'v0.7.8.4',
    date: '2026-01-18',
    changes: [
      'Fix CRITICAL: Resolved TypeError (partners.filter) in PartnerAttributionCard by correctly extracting .data from getPartners response.',
      'Fix: Ensure Customer Detail Page loads correctly without crashing on partner data.'
    ],
  },
  {
    version: 'v0.7.8.3',
    date: '2026-01-18',
    changes: [
      'Fix CRITICAL: Resolved ReferenceError (loadCustomerData) in CustomerDetailPage by passing callback properly to OverviewTab.',
      'Fix: Ensure customer details page loads correctly without white screen crash.'
    ],
  },
  {
    version: 'v0.7.8.2',
    date: '2026-01-17',
    changes: [
      'Fix CRITICAL: Resolved Temporal Dead Zone (ReferenceError) crash in CustomerDetailPage caused by hook ordering.',
    ],
  },
  {
    version: 'v0.7.8.1',
    date: '2026-01-17',
    changes: [
      'Fix: Freshdesk Import - Resolved ReferenceError (loadCustomerData) preventing customer enrichment.',
      'Fix: CRM Search - Resolved ambiguous column error in crm_search_parties DB function.',
    ],
  },
  {
    version: 'v0.7.8.0',
    date: '2026-01-17',
    changes: [
      'Fix: KB Ingestion now reads Freshdesk credentials from api_credentials table (same as freshdesk-gateway)',
      'Fix: Removed dependency on secret_store table which was not populated for Freshdesk',
      'Fix: Domain normalization added (strips https:// and .freshdesk.com)',
    ],
  },
  {
    version: 'v0.7.7.9',
    date: '2026-01-17',
    changes: [
      'Fix: KB Backfill now actually calls edge function (was only updating DB status)',
      'Fix: Freshdesk credentials now read from env vars (FRESHDESK_DOMAIN, FRESHDESK_API_KEY)',
      'Fix: handleStartBackfill invokes startFDNotesIngestionStreaming to process tickets',
    ],
  },
  {
    version: 'v0.7.7.8',
    date: '2026-01-17',
    changes: [
      'Fix: KB Edge Function 500 Error - Fixed openaiKey.credential_value bug (was already a string)',
      'Fix: Added better error diagnostics with debug_step tracking to identify failure points',
      'Fix: OpenAI/OpenRouter key lookup now properly extracts credential_value from array result',
    ],
  },
  {
    version: 'v0.7.7.7',
    date: '2026-01-17',
    changes: [
      'UX: KB Ingestion now auto-shows Automation Log immediately when clicking buttons (like Banking page)',
      'UX: Removed manual "View Live Logs" button - monitor appears automatically',
      'Fix: Replaced KBIngestionMonitor with AutomationMonitor for consistent UX',
      'Fix: Real-time log subscription streams logs from database to UI',
      'UX: Added Stop button to cancel running ingestion',
    ],
  },
  {
    version: 'v0.7.7.6',
    date: '2026-01-17',
    changes: [
      'Fix: KB Edge Function 500 Error - Fixed ai-client.ts to include kb_classify_content task type',
      'Fix: Deployed kb-ingest-fd-notes-streaming and kb-classify-content edge functions',
      'Fix: Updated kb_ingestion_streaming migration to include all knowledge_entries columns',
      'Deploy: Applied database migrations and edge functions to Supabase',
    ],
  },
  {
    version: 'v0.7.7.5',
    date: '2026-01-16',
    changes: [
      'Fix: KB Resume Backfill - Now directly invokes edge function and opens monitor',
      'Fix: findBackfillRunId - Falls back to ANY freshdesk_note run if no full_backfill exists',
      'UX: Improved warning message when no runs exist',
    ],
  },
  {
    version: 'v0.7.7.4',
    date: '2026-01-16',
    changes: [
      'Fix: KB Live Logs - Fixed "t is not a function" error caused by wrong toast API usage',
      'Fix: Updated toast calls to use correct useToast hook API (showSuccess, showError, showWarning)',
      'Fix: findBackfillRunId - Replaced unsupported .maybeSingle() with array access',
    ],
  },
  {
    version: 'v0.7.7.3',
    date: '2026-01-16',
    changes: [
      'Fix: KB Live Logs - Fixed critical "apiClient.from is not a function" error by using supabase client directly',
      'Fix: knowledgeBaseService.ts - Replaced apiClient.from() and apiClient.rpc() with supabase equivalents',
      'UX: Added loading spinners to Start/Pause/Resume backfill buttons',
      'UX: Added loading state to View Logs button',
    ],
  },
  {
    version: 'v0.7.7.2',
    date: '2026-01-16',
    changes: [
      'Fix: KB Live Logs monitor - Added fallback run ID lookup when last_run_id is null',
      'Fix: View Live Logs / View Last Run Logs buttons now work even for backfills started before update',
      'Fix: Shows toast message if no backfill runs found yet',
    ],
  },
  {
    version: 'v0.7.7.1',
    date: '2026-01-16',
    changes: [
      'Feature: Banking Copilot V3 - Full AI Assistant with 12 supported intents',
      'Feature: Rich context injection - AI now knows your categories, suppliers, accounts, pending statements',
      'Feature: MATCH_TRANSACTIONS - Match bank transactions against supplier invoices/statements/PNT',
      'Feature: RECONCILE_STATEMENT - Deep reconcile supplier statements with one command',
      'Feature: FIND_DUPLICATES - Detect potential duplicate transactions',
      'Feature: ANALYZE_SPENDING - Get spending insights by category/period',
      'Feature: AUTO_MATCH_ALL - Run intelligent auto-matching on all unmatched transactions',
      'Feature: EXPORT_DATA - Export transactions to CSV',
      'Feature: Enhanced action cards - Shows intent icons, confidence, estimated affected count, warnings',
      'Feature: Supplier intelligence - AI recognizes AerTicket, BA, and routes matching appropriately',
    ],
  },
  {
    version: 'v0.7.7.0',
    date: '2026-01-16',
    changes: [
      'Feature: Sabre AI Agent V2 - World-class PNR & Travel Operations Assistant',
      'Feature: Travel Request Parser - Parses complex customer emails into structured change requests',
      'Feature: RTW Change Planner - Multi-segment rebooking with availability checking and cost estimation',
      'Feature: Insurance Compliance Validator - 12hr max flight rule, overnight stop requirements',
      'Feature: RTW Routing Validator - Segment limits, continent limits, oneworld carrier validation',
      'Feature: Cabin Upgrade Checker - First/Business class availability checking',
      'Feature: GDS Formatting Service - Long Sell conversion for Amadeus & Sabre',
      'Feature: Travel Request Plan UI - Rich display of change plans with execution controls',
      'Feature: Freshdesk-to-AI Integration - Load and parse tickets directly',
      'Added: Corp codes for Finnair, AA, AF/KL, Emirates, Etihad, Cathay Pacific',
      'Added: RTW OSI entries and retention line generation',
      'Navigation: New "Sabre AI Agent" in AI Tools menu',
    ],
  },
  {
    version: 'v0.7.6.32',
    date: '2026-01-15',
    changes: [
      'Fix: WhatsApp Import Timeout - Optimized for large imports (1000+ messages) by using batch operations.',
      'Fix: Deduplication now uses single query instead of per-message checks.',
      'Fix: AI analysis skipped for imports >200 messages to prevent 504 Gateway Timeout.',
      'Fix: Messages inserted in batches of 500 for efficiency.',
    ],
  },
  {
    version: 'v0.7.6.31',
    date: '2026-01-15',
    changes: [
      'Fix: OpenRouter AI model ID - Changed to claude-sonnet-4 (was using invalid claude-opus-4-5).',
      'Fix: AI JSON parsing - Added cleanJsonResponse() to handle markdown code block wrappers in AI responses.',
      'Feature: PNT Payment Source Tracking - Track HOW PT paid for each transaction (client card, supplier credit, PT bank, etc.).',
      'Migration: Added payment_source fields, detection functions, and audit logging to pnt_transactions.',
    ],
  },
  {
    version: 'v0.7.6.30',
    date: '2026-01-15',
    changes: [
      'Fix: Statement Import - Graceful handling of duplicate key constraint violations.',
      'Fix: Credit/Debit type display - Now correctly uses "type" field with fallback to "transaction_type".',
      'Fix: Color coding - Credits (money in) are green, Debits (money out) are red.',
      'Migration: Fixes existing FORA, Club Med, Biosse transactions to show as credits (incoming payments).',
    ],
  },
  {
    version: 'v0.7.6.26',
    date: '2026-01-15',
    changes: [
      'Fix: Gmail Scan now runs banking-auto-match FIRST before searching for receipts.',
      'Fix: Transactions already matched (internal transfers, salary, Stripe, CRM) are skipped from Gmail scan.',
      'This prevents wasting Gmail API calls on transactions that should not need receipt matching.',
    ],
  },
  {
    version: 'v0.7.6.25',
    date: '2026-01-15',
    changes: [
      'Fix: Bank transaction sync - Fixed upsert conflict column (using external_id instead of bank_account_id,transaction_id).',
      'Fix: Brex, Mercury, Revolut, Wise sync - All now correctly populate external_id for proper upsert deduplication.',
      'This fixes the issue where transactions were not appearing despite successful sync.',
    ],
  },
  {
    version: 'v0.7.6.24',
    date: '2026-01-15',
    changes: [
      'Banking: Auto-Match enhancements - Internal transfer detection ("Segregated Customer" patterns).',
      'Banking: Salary payment recognition (Gebie Arcenal and known payees).',
      'Banking: CRM customer name matching for incoming credits/refunds.',
      'Banking: Stripe charge matching for transactions containing "stripe".',
      'Banking: USD/GBP currency conversion in Gmail receipt matching (Twilio, Bolt, etc.).',
    ],
  },
  {
    version: 'v0.7.6.23',
    date: '2026-01-15',
    changes: [
      'Fix: Wise Profile ID - Strip "P" prefix from profile IDs (Wise API uses numeric IDs only).',
      'Fix: Brex debug logging - Added raw response structure logging to diagnose empty transactions.',
      'Debug: Enhanced transaction fetch logging for troubleshooting.',
    ],
  },
  {
    version: 'v0.7.6.22',
    date: '2026-01-15',
    changes: [
      'Fix: Brex transactions - Added pagination to fetch ALL transactions (was only fetching first page).',
      'Fix: Brex logging - Added comprehensive debug logging for transaction counts.',
      'Fix: Wise 404 error - Better error message explaining multi-currency account requirement.',
      'Fix: Wise profile detection - Improved auto-detect with detailed logging.',
      'Fix: Banking Batch Builder - Corrected debit/credit color display (red for debit, green for credit).',
    ],
  },
  {
    version: 'v0.7.6.21',
    date: '2026-01-15',
    changes: [
      'Fix: Brex direct API - Banking sync now uses direct Brex API instead of Xero bank feeds (priority fix).',
      'Fix: Wise fallback endpoints - Added v3 and legacy borderless-accounts fallback for accounts without v4 API access.',
      'Fix: Wise statements - Added legacy endpoint fallback for balance-statements when 404 occurs.',
      'Fix: Banking sync logic - Direct API integrations now take priority over Xero bank feeds in syncAccount().',
    ],
  },
  {
    version: 'v0.7.6.20',
    date: '2026-01-15',
    changes: [
      'Fix: Wise integration - Updated to v4 balances API, fixed 404 error from deprecated borderless-accounts endpoint.',
      'Fix: Wise statements - Corrected endpoint path to /balance-statements/ (was using deprecated /borderless-accounts/).',
      'Fix: Brex integration - Improved error messages with specific scope requirements (accounts.cash.readonly + transactions.cash.readonly).',
      'Docs: Updated integration setup instructions for Brex and Wise with correct API documentation links.'
    ],
  },
  {
    version: 'v0.7.6.19',
    date: '2026-01-15',
    changes: [
      'Fix: Amazon batch matching timeouts - Reduced batch size from 10 to 5 transactions to prevent Edge Function timeouts.',
      'Fix: Added retry logic for failed batches with smaller chunks.',
      'UX: Real-time logging improvements for Deep Scan visibility.'
    ],
  },
  {
    version: 'v0.7.6.17',
    date: '2026-01-15',
    changes: [
      'Fix: Banking Auto Match - Added "auto_match_transfer" to `categorization_method` DB enum to resolve insert errors.'
    ],
  },
  {
    version: 'v0.7.6.16',
    date: '2026-01-15',
    changes: [
      'Banking: Added "Auto Match Internal Transfers" - One-click detection and matching of internal transfers between accounts.',
      'Banking: Integrated Glass Box Process Monitor for transfer matching transparency.'
    ],
  },
  {
    version: 'v0.7.6.15',
    date: '2026-01-15',
    changes: [
      'Banking: Critical Fix for Uber/HTML receipts. Improved "Fragmented DOM" parsing where "Total" and Amount are separated by tags.',
      'Banking: Enhanced Regex to span across newlines for Total detection.',
      'Gmail: Added Body Preview to failure reasons for better debugging.'
    ],
  },
  {
    version: 'v0.7.6.14',
    date: '2026-01-15',
    changes: [
      'Glass Box: Auto Match Parsed now shows detailed execution trace',
      'Finance: Stop button added to Auto Match and Reparse processes',
      'Banking: Fixed Gmail receipt search for Uber (HTML support)',
      'Invoice: Fixed duplicate invoice reparse issue',
    ],
  },
  { version: 'v0.7.6.12', date: '2026-01-14', notes: 'Critical Fix: Gmail Parsing - Added HTML/Text fallback logic. Prevents "Empty Email" errors for HTML-only receipts (e.g., Uber) by explicitly extracting and stripping HTML tags.' },
  { version: 'v0.7.6.11', date: '2026-01-14', notes: 'Fix: AI Cost Optimization - Pre-check duplicates before AI execution. Fix: Gmail Search - Added "Amount + Merchant" specific search to find Uber/high-volume receipts.' },
  { version: 'v0.7.6.10', date: '2026-01-14', notes: 'Fix: Invoice duplicate check - Now uses supplier_code matching DB constraint. Feature: Stop button in ProcessMonitor for aborting reparse. UX: Prevents duplicate constraint violations from wasting AI credits.' },
  { version: 'v0.7.6.9', date: '2026-01-14', notes: 'UX: Enhanced Deep Scan Transparency - Shows which transactions are queued, per-transaction outcomes with search terms and failure reasons, fixed find_transaction_matches RPC.' },
  { version: 'v0.7.6.8', date: '2026-01-14', notes: 'Fix: Supplier Invoice issues - Removed "Upload supplier" dropdown (AI auto-identifies), Redeployed parse-supplier-invoice function (fixed 503 errors), Enhanced Gmail parsing fallback for Amazon.' },
  { version: 'v0.7.6.5', date: '2026-01-14', notes: 'Feature: PNR Contact Update - Added "Edit" mode to Contact Information panel allowing email updates and adding phones. Fix: Added auto-refresh on PNR update.' },
  { version: 'v0.7.5.9', date: '2026-01-14', notes: 'Feature: RTW Carrier Pricing Panel (WPRW¥A commands). Fix: PNR parsing (phone filter, FD tickets, OSI/SSR), RTW distance enrichment, airline logos.' },
  { version: 'v0.7.5.8', date: '2026-01-14', notes: 'Fix: PNR Import - Resolved "Copy to RTW" runtime error (`useRef`) and improved parsing (OSI/SSR, Contact Filtering, FD Ticket Numbers).' },
  { version: 'v0.7.5.7', date: '2026-01-14', notes: 'Critical Fix: RTW Distance Calculation - DB-backed distance enrichment replaces 2000mi fallback. Print View: FI→AY label, improved logos, times display. StatusTimeline: Flight numbers, logos, BA points.' },
  { version: 'v0.7.5.6', date: '2026-01-14', notes: 'Feature: RTW StatusTimeline - Flight numbers, logos, times, stopover badges, BA points. RTW Print View - Departure/arrival columns, tier points per segment, totals row. UI: Right column overflow fix.' },
  { version: 'v0.7.5.4', date: '2026-01-14', notes: 'Feature: RTW Summary Print View & Fix: Freshdesk AI Response Handling (JSON parse protection).' },
  { version: 'v0.7.5.3', date: '2026-01-14', notes: 'Cross-Module Bug Fixes: Banking page import fix, RTW sync/map print/timeline dates/layout fixes, CRM Customer Since date now uses earliest interaction.' },
  { version: 'v0.7.5.2', date: '2026-01-13', notes: 'Feature: Recurring Transaction Detection - Auto-identifies monthly/weekly subscriptions with frequency prediction and visual indicators.' },
  { version: 'v0.7.5.1', date: '2026-01-13', notes: 'Feature: Banking Batch Intelligence - Smart Transfer Detection, Internal Transfer Auto-Match, and improved Merchant Name Normalization for cleaner batching.' },
  { version: 'v0.7.5.0', date: '2026-01-13', notes: 'Feature: Banking Automation V2 - Self-Learning Rule Engine, AI Copilot, Command Center with Manual/Batch controls, and Operational Enhancements (Bulk Ignore).' },
  { version: 'v0.7.4.8', date: '2026-01-13', notes: 'Fix: RTW Continent Validation - Implemented DB-backed continent mapping for global RTW validation.' },
  { version: 'v0.7.4.7', date: '2026-01-13', notes: 'Feature: Banking Automation Logs - Added real-time visible logs for AI and Matching activities.' },
  { version: 'v0.7.4.6', date: '2026-01-13', notes: 'UI Improvement: Banking Dashboard - Added exact count for unmatched transactions and prominent "Deep Scan" text button.' },
  { version: 'v0.7.4.5', date: '2026-01-13', notes: 'Fix: Build System - Resolved syntax error (double finally block) in ConnectionsSettings.tsx that prevented production build.' },
  { version: 'v0.7.4.4', date: '2026-01-13', notes: 'Fix: Gmail Connectivity - Added "Test Connection" button, improved search flexibility (numbers preserved), and Deep Scan capability.' },
  { version: 'v0.7.4.3', date: '2026-01-13', notes: 'Fix: Gmail Integration - Added debug visibility (Query/Dates) and explicit error handling for missing Auth/Tokens.' },
  { version: 'v0.7.4.1', date: '2026-01-12', notes: 'Feature: Gmail Integration - Added "Find Receipt" (contextual) and "Scan Backlog" (bulk) buttons to Transaction Matcher.' },
  { version: 'v0.7.4.0', date: '2026-01-12', notes: 'Feature: Intelligent Banking Categorization - Hybrid AI/Rule engine, Gmail Receipt Parsing, and Enhanced Transaction Matching with Owner Attribution.' },
  { version: 'v0.7.3.11', date: '2026-01-12', notes: 'Fix: Audit Modal - Included grouped transaction members in audit calculations.' },
  { version: 'v0.7.3.10', date: '2026-01-12', notes: 'Feature: Enhanced Audit Modal - Added Client, ID, Payment Date, Supplier columns. Added Research/Refund toggle.' },
  { version: 'v0.7.3.9', date: '2026-01-12', notes: 'Fix: Audit Modal TDZ - Reordered useMemo before useEffect to prevent ReferenceError.' },
  { version: 'v0.7.3.8', date: '2026-01-12', notes: 'Fix: Audit Modal Crash - Resolved ReferenceError (excludedItems) in Potential Profit calculation.' },
  { version: 'v0.7.3.7', date: '2026-01-12', notes: 'Fix: PNT Audit - Ensure Audit Modal properly renders when clicking summary boxes.' },
  { version: 'v0.7.3.6', date: '2026-01-12', notes: 'Feature: PNT Audit - Full implementation of Advanced Auditing (Cost Aging, Histograms, Exclusions) and fix for Sabre PNR Display.' },
  { version: 'v0.7.3.5', date: '2026-01-12', notes: 'Fix: RateHawk Sync - Corrected hotel name lookup, amount extraction, and added Voucher Download feature.' },
  { version: 'v0.7.3.2', date: '2026-01-10', notes: 'Fix: Supplier invoice upload + ingestion - enforce supplier_code (PICUS), normalize Picasso codes, add DB auto-fill trigger to prevent NOT NULL failures.' },
  { version: 'v0.7.3.1', date: '2026-01-10', notes: 'Feature: Picasso Travel Integration - Added generic invoice parser (USD support) and client-side PNR parsing. Fix: Supplier Modal Z-Index.' },
  { version: 'v0.7.3.0', date: '2026-01-09', notes: 'Deployment: Synced latest changes (RateHawk, Functions) and verified build integrity.' },
  { version: 'v0.7.2.9', date: '2026-01-09', notes: 'Fix: Build & A11y - Resolved accessibility issues and deployment blockers.' },
  { version: 'v0.7.2.8', date: '2026-01-09', notes: 'Fix: RTW Planner PNR Import - Resolved "No segments found" error by correctly parsing nested API response structure.' },
  { version: 'v0.7.2.7', date: '2026-01-08', notes: 'PNT Fix: PNT Company Dropdown - Resolved empty dropdown issue by correcting column name mismatch (name -> company_name) in PNTListPage.' },
  { version: 'v0.7.2.6', date: '2026-01-08', notes: 'PNT Fix: Moved Deduplication to start of Automation run. Ensured Invoice Matches persist immediately.' },
  { version: 'v0.7.2.5', date: '2026-01-08', notes: 'PNT Fixes: Deduplication (DB-wide scan), Smart Match (Deep Scan conversations), Auto-Ticketing (Status update on high match).' },
  { version: 'v0.7.2.4', date: '2026-01-08', notes: 'PNT Deduplication & Enrichment: Automatic deduplication of Strips/Manual PNTs, Backfilling actuals from invoices/statements (AerTicket), and Profit/Loss reason tracking (ADM, Service Fee, etc).' },
  { version: 'v0.7.2.3', date: '2026-01-08', notes: 'Fix: Brex & Wise Integrations - Corrected Brex API endpoint to /v2/accounts/cash, enabled direct Wise credential entry (removed Pipedream redirect), auto-detect Wise Profile ID, added comprehensive debug logging.' },
  { version: 'v0.7.1.3', date: '2026-01-07', notes: 'Fix: Xero Zero Data - Implemented "Global Data Check". If sync returns 0, bypasses account filters to verify organization data existence. Added Debug Trace to UI.' },
  { version: 'v0.7.1.1', date: '2026-01-07', notes: 'Fix: Xero Sync - Resolved 0 transactions issue by implementing bank account discovery. Now fetches all accounts first, then queries each individually.' },
  { version: 'v0.7.1.0', date: '2026-01-07', notes: 'PNT Enrichment Complete: Ticketed PNT backfilling from invoices/statements, Profit/Loss tracking with categorized reasons, @propellertravel.com email filtering.' },
  { version: 'v0.7.0.4', date: '2026-01-07', notes: 'B2B Support: Companies now appear in customer dropdown, auto-match employees to companies via email domain (e.g., Annie Roberts -> Open Capital via @opencapital.com).' },
  { version: 'v0.7.0.3', date: '2026-01-07', notes: 'Feature: Xero Sync Diagnostics - Comprehensive activity viewer showing which accounts matched, which failed, and why. Real-time diagnostic logs with actionable fix suggestions.' },
  { version: 'v0.7.0.2', date: '2026-01-07', notes: 'Configuration: Xero Sync - Increased sync lookback window to 365 days to ensure historical transactions are imported.' },
  { version: 'v0.7.0.1', date: '2026-01-07', notes: 'Fix: Xero Bank Feeds - Seamlessly sync Revolut/Brex bank data via Xero. Implemented intelligent account auto-linking, smart dashboard routing, and blocked direct API calls for Xero-managed feeds.' },
  { version: 'v0.7.0.0', date: '2026-01-07', notes: 'PNT Smart Matching Complete: All 25 improvements implemented (Tier 1-3). Includes Trip Mining, Traveler Matching, Smart Confidence Scoring, and AI workflow integration.' },
  { version: 'v0.6.1.3', date: '2026-01-07', notes: 'PNT Smart Matching Tier 2: Supplier Invoice cross-reference, Statement Line matching, PNR Record integration for enhanced auto-enrichment.' },
  { version: 'v0.6.1.2', date: '2026-01-07', notes: 'PNT Smart Matching Tier 1: Cascading FD search, nickname variants, ticket context scoring, fuzzy customer matching, email domain matching, Stripe description parsing, enhanced automation logs with match signals.' },
  { version: 'v0.6.1.1', date: '2026-01-06', notes: 'PNT Automation: Fixed FD search API response, Run All button (auto-match + FD scan with rate limiting), Automation Log panel, CRM match green checkmark indicator.' },
  { version: 'v0.6.1.0', date: '2026-01-06', notes: 'Feature: Xero Auto-Sync - Sync from Xero button, auto-sync on Banking page load, hourly auto-sync.' },
  { version: 'v0.6.0.9', date: '2026-01-06', notes: 'PNT Enhancements: FD Search Modal (search/preview tickets), Inline Editing (FD#/PNR), Auto Match Parsed (invoices + customers with name memory)' },
  { version: 'v0.6.0.7', date: '2026-01-06', notes: 'Fix: Xero Direct OAuth - Updated Integrations page to use direct OAuth flow instead of Pipedream wizard.' },
  { version: 'v0.6.0.5', date: '2026-01-05', notes: 'Feature: Freshdesk Bulk Import - Added bulk import option for multiple FD ticket numbers or email addresses with full import and enrichment process, duplicate checking.' },
  { version: 'v0.6.0.4', date: '2026-01-05', notes: 'Fix: RTW Itinerary Builder - Added explicit React import to RTWPlannerPage to fix production ReferenceError.' },
  { version: 'v0.6.0.3', date: '2026-01-05', notes: 'Fix: RTW Itinerary Builder deployment - consolidated React fixes for unmount errors and list keys.' },
  { version: 'v0.6.0.2', date: '2026-01-05', notes: 'Hotfix: Fixed "React is not defined" error by enforcing explicit React imports in RTW components.' },
  { version: 'v0.6.0.1', date: '2026-01-05', notes: 'Fix: RTW Itinerary Builder - Resolved React errors (unmount updates) and list key issues.' },
  { version: 'v0.6.0', date: '2026-01-05', notes: 'Major Release: Freshdesk 2.0 (Real-time Sync, Two-way control, AI Sentiment/Summaries), Customer Merge, VIP Queue, Global Search, CSAT.' },
  { version: 'v0.5.5.97', date: '2026-01-05', notes: 'Feature: Process Monitor - Visual feedback for long-running operations like Auto-Match. Integrated logs & progress bar.' },
  { version: 'v0.5.5.96', date: '2026-01-05', notes: 'Feature: WhatsApp Chat Import - Parse exports, deduplicate messages, AI analysis & Freshdesk ticket creation' },
  { version: 'v0.5.5.95', date: '2026-01-05', notes: 'Feature: Customer Search - Implemented fuzzy search (pg_trgm) for better typo tolerance' },
  { version: 'v0.5.5.94', date: '2026-01-05', notes: 'Fix: PDF parsing - added explicit Buffer import from node:buffer to fix "Buffer is not defined" error in Deno' },
  { version: 'v0.5.5.93', date: '2026-01-05', notes: 'Fix: PDF parsing - switched to npm:pdf-parse with Buffer buffering for native Node compat in Deno (resolves 500 crash)' },
  { version: 'v0.5.5.92', date: '2026-01-05', notes: 'Fix: PDF parsing - replaced pdfjs-dist with pdf-parse (via esm.sh) for reliable text extraction without 500 errors' },
  { version: 'v0.5.5.91', date: '2026-01-05', notes: 'Fix: PDF parsing - reverted to pdfjs-dist with improved error handling for scanned PDFs (avoids 500 errors)' },
  { version: 'v0.5.5.90', date: '2026-01-05', notes: 'Stripe Sync: Fixed duplicate key error - improved deduplication to check pnt_id pattern' },
  { version: 'v0.5.5.89', date: '2026-01-05', notes: 'Fix: PDF parsing - replaced unpdf/esm.sh with pdfjs-serverless/jsdelivr for Deno Edge Function compatibility' },
  { version: 'v0.5.5.88', date: '2026-01-05', notes: 'Stripe Sync: Added 180/365 day lookback options, enhanced debug logs showing UK & US API connection status' },
  { version: 'v0.5.5.86', date: '2026-01-05', notes: 'Feature: Multi-file upload with deduplication (skips already uploaded files)' },
  { version: 'v0.5.5.85', date: '2026-01-05', notes: 'Fix: Complete supplier_statement_lines schema with all columns' },
  { version: 'v0.5.5.84', date: '2026-01-05', notes: 'Fix: Dropped ALL NOT NULL constraints on supplier_statements (except id, user_id)' },
  { version: 'v0.5.5.75', date: '2026-01-04', notes: 'Stripe Multi-Region Support' },
  { version: 'v0.5.5.75', date: '2026-01-04', notes: 'Stripe: Multi-Region Support (US/UK) & Bank Account Separation' },
  { version: 'v0.5.5.74', date: '2026-01-02', notes: 'Statement Reconciliation: Unified Line-Item Grid, Search/Filter, Strict Deduplication' },
  { version: 'v0.5.5.68', date: '2026-01-02', notes: 'Enhancements: Multi-File Upload, Progress Bar, Dispute Actions' },
  { version: 'v0.5.5.67', date: '2026-01-02', notes: 'Parser: Extract Balance & Payments from Weekly Stmts' },
  { version: 'v0.5.5.66', date: '2026-01-02', notes: 'Fixed AerTicket Statement Parsing & Weekly Format Support' },
  { version: 'v0.5.5.65', date: '2026-01-02', notes: 'Fixed Statement Reconciliation currency RangeError' },
  { version: 'v0.5.5.64', date: '2026-01-02', notes: 'Fixed PNT Dashboard 503 errors and optimized performance' },
];

/**
 * Get the full version string with build timestamp
 * Format: VERSION/BUILD_TIMESTAMP (e.g., "v0.8.2.28/30JAN2340UK")
 */
export function getVersionString(): string {
  return `${APP_VERSION}/${BUILD_TIMESTAMP}`;
}

/**
 * Get the full version string with git SHA
 * Format: VERSION/BUILD_TIMESTAMP (SHA) (e.g., "v0.8.2.28/30JAN2340UK (a1b2c3d)")
 */
export function getFullVersionString(): string {
  return `${APP_VERSION}/${BUILD_TIMESTAMP} (${GIT_SHA})`;
}

/**
 * Get just the version number (without build timestamp)
 */
export function getVersion(): string {
  return APP_VERSION;
}

/**
 * Get just the build timestamp
 */
export function getBuildTimestamp(): string {
  return BUILD_TIMESTAMP;
}

/**
 * Get the git commit SHA (short)
 */
export function getGitSha(): string {
  return GIT_SHA;
}

/**
 * Get the ISO build date
 */
export function getBuildDate(): string {
  return BUILD_TIMESTAMP;
}


